import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Spinner,
  Alert,
  Container,
  Card,
  CardBody,
  Badge,
  Modal,
  ModalBody,
  Table,
  Input,
  Label,
  Row,
  Col,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  ModalHeader,
  CardHeader,
  ModalFooter,
  FormGroup,
  Form,
  Progress,
} from 'reactstrap';
import Tooltip from "@mui/material/Tooltip";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import {
  getTeamLeadProjects,
  getTeammember,
  getTLProjectDetails,
  OverallLogDTO,
  getTaskLogs,
  getSubTaskLogs,
  getUnreadCount,
  getComments,
  getTaskAuditLogs,
  getSubTaskAuditLogs,
  deleteTask,
  CreateTimeSheetDTO,
  updateTimeSheet,
  createTimeSheet,
  getProjectNames,
  updateMyTask,
  updateMySubTask,
  getChildSubTaskById,
  ChildSubTaskViewDTO,
  updateMyChildSubTask,
  ChildSubTaskUpdateTMDto,
  deleteChildSubTask,
  deleteSubTask,
  getChildSubTaskLogs,
  getCanCreate,
} from '../../Api/ApiUrlConst';
import { useAuth } from '../../Context/AuthContext';
import AddTaskSubTaskComponents from './AddTaskSubTaskComponents';
import AddSubTaskInline from './AddSubTaskComponents';
import EditTaskComponents from './EditTaskComponents';
import EditSubTaskComponent from './EditSubTaskComponent';
import { CheckCircleIcon, Edit as EditIcon, FileQuestionMarkIcon, Sheet, Upload, UploadCloud } from 'lucide-react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import Comments from '../CommentComponent/Comments';
import SettingsIcon from '@mui/icons-material/Settings';
import MarkChatUnreadIcon from "@mui/icons-material/MarkChatUnread";
import ChatIcon from '@mui/icons-material/Chat';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import HttpsIcon from '@mui/icons-material/Https';
import ClientInputModal from '../ClientInput/ClientInputModal';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AddAlarmIcon from '@mui/icons-material/AddAlarm';
import { Add } from '@mui/icons-material';
import EditChildSubTaskInline from './EditChildSubTaskInline';
import AddChildSubTaskInline from './AddChildSubTaskInline';
import { ChildSubTask } from '../TMComponents/TeamMemberTasks';
import { ChildSubTaskDTO } from '../../types/client.types';




//#region  Interfaces
interface ProjectDTO {
  id: number;
  projectID: string;
  projectName: string;
  createdByName: string;
  startDate: string;
  endDate: string;
  taskCount: number;
  tasks?: TaskDTO[];
}

interface TaskDTO {
  id: number;
  description: string;
  projectId: number;
  projectName: string;
  assignedToId: string;
  assignedToName: string;
  createdById: string;
  createdByName: string;
  createdByDepartment: string;
  dueDate: string;
  completionDate: string;
  status: string;
  priority: string;
  percentCompleted: number;
  remarks: string;
  issues: string;
  subTasks: SubTaskDTO[];
  totalLogs?: number;   // 👈 MUST be here (not outside/after subTasks)
  isDueDateLocked: boolean;
  isClientInputEnabled?: boolean;
  spentHours?: number;
  maxHours?: number;
  totalSpent?: number;   // ✅ ADD
  totalMax?: number;     // ✅ ADD
}

interface CurrentUser {
  id: string;
  name: string;
}

interface TimeSheetRow {
  date?: string;
  projectName?: string;
  module?: string;
  product?: string;
  part?: string;
  hours?: number;
  assignedBy?: string;
  description?: string;

  draftingType?: string;
  quantity?: number;
  totPages?: number;
  subTask?: string;
  childSubTask?: string;
  taskId?: number;
  subTaskId?: number;
  taskDescription?: string;
  subTaskDescription?: string;
  childSubTaskDescription?: string;
  createdByName?: string;
}



interface SubTaskDTO {
  id: number;
  taskId: number;
  parentSubTaskId: number | null;
  description: string;
  assignedToId: string;
  assignedToName: string;
  status: string;
  priority: string;
  dueDate: string;
  percentCompleted: number;
  qcProgress?: number;   // ✅ <-- add this line
  workHours: number;
  completionDate: string | null;
  remarks: string;
  issues: string;
  totalLogs?: number;   // 👈 MUST be here (not outside/after subTasks)
  isDueDateLocked: boolean;
  isClientInputEnabled: boolean;
  spentHours?: number;
  maxHours?: number;
  createdByName?: string;
  childSubTasks?: ChildSubTask[];
  totalSpent?: number;   // ✅ ADD
  totalMax?: number;
}




interface TeamMemberDTO {
  userID: string;
  name: string;
  department: string;
}

interface SortConfig {
  key: string | null;
  direction: 'asc' | 'desc';
}

//#region Status CSS

const statusColorMap: Record<string, string> = {
  'Yet to Start': 'status-yet-to-start',
  YetToStart: 'status-yet-to-start',

  'On Hold': 'status-on-hold',
  OnHold: 'status-on-hold',

  'Waiting for Input': 'status-waiting-input',
  WaitingForInput: 'status-waiting-input',

  'Modelling in Progress': 'status-modelling-progress',
  ModellingInProgress: 'status-modelling-progress',

  'Modelling ToDo': 'status-modelling-Todo',
  ModellingTodo: 'status-modelling-Todo',

  'Modelling Completed': 'status-modelling-completed',
  ModellingCompleted: 'status-modelling-completed',

  'Drafting in Progress': 'status-drafting-progress',
  DraftingInProgress: 'status-drafting-progress',

  'Drafting ToDo': 'status-drafting-Todo',
  DraftingTodo: 'status-drafting-Todo',

  'Drafting Completed': 'status-drafting-completed',
  DraftingCompleted: 'status-drafting-completed',

  'QC ToDo': 'status-qc-Todo',
  QCTodo: 'status-qc-Todo',

  'QC in Progress': 'status-qc-progress',
  QCInProgress: 'status-qc-progress',

  'QC Completed': 'status-qc-completed',
  QCCompleted: 'status-qc-completed',

  'Client Review in Progress': 'status-client-review-progress',
  ClientReviewInProgress: 'status-client-review-progress',

  'Client Review ToDo': 'status-client-review-Todo',
  ClientReviewTodo: 'status-client-review-Todo',



  'Client Review Completed': 'status-client-review-completed',
  ClientReviewCompleted: 'status-client-review-completed',

  'Revision in Progress': 'status-Revision-progress',
  RevisionInProgress: 'status-Revision-progress',

  'Revision ToDo': 'status-Revision-Todo',
  RevisionTodo: 'status-Revision-Todo',

  'Revision Completed': 'status-Revision-completed',
  RevisionCompleted: 'status-Revision-completed',


  'Final Correction in Progress': 'status-final-correction-progress',
  FinalCorrectionInProgress: 'status-final-correction-progress',

  'Final Correction ToDo': 'status-final-correction-Todo',
  FinalCorrectionTodo: 'status-final-correction-Todo',

  'Final Correction Completed': 'status-final-correction-completed',
  FinalCorrectionCompleted: 'status-final-correction-completed',

  'Not In Scope': 'status-Not-In-Scope',
  NotInScope: 'status-Not-In-Scope',

  Released: 'status-released',

  'Completed & Closed': 'status-completed',
  Completed: 'status-completed',

  Cancelled: 'status-cancelled',
  Issues: 'status-issues',

 CompletedAndReleased: "status-completedandreleased",


  NotApplicable1: "status-Drafting-Released",
};


const priorityColorMap: Record<string, string> = {
  Low: 'priority-low',
  Medium: 'priority-medium',
  High: 'priority-high',
  Critical: 'priority-critical',
};

//#region Condition

const TeamLeadProjectsWithTasks: React.FC = () => {
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: 'asc' });
  const { role, userID, department, name } = useAuth();
  const [projects, setProjects] = useState<ProjectDTO[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectError, setProjectError] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [tasksError, setTasksError] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMemberDTO[]>([]);
  const [showSubtasksForTask, setShowSubtasksForTask] = useState<Record<number, boolean>>({});
  const [inlineEditTaskId, setInlineEditTaskId] = useState<number | null>(null);
  const [inlineEditSubTaskId, setInlineEditSubTaskId] = useState<number | null>(null);
  const [inlineNewTaskProjectId, setInlineNewTaskProjectId] = useState<number | null>(null);
  const [inlineNewSubtaskTaskId, setInlineNewSubtaskTaskId] = useState<Record<number, boolean>>({});
  const [remarksModalOpen, setRemarksModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRemarks, setSelectedRemarks] = useState('');
  const [showInlineCreateTask, setShowInlineCreateTask] = useState(false);
  const [showMyTasksOnly, setShowMyTasksOnly] = useState(false);
  const [showNewRow, setShowNewRow] = useState(false);
  const [newRowData, setNewRowData] = useState<TimeSheetRow>({});
  const [inlineNewChildSubTaskId, setInlineNewChildSubTaskId] = useState<Record<number, boolean>>({});
  const [expandedSubTasks, setExpandedSubTasks] = useState<Record<number, boolean>>({});
  const [childTasks, setChildTasks] = useState<{ [subTaskId: number]: ChildSubTaskViewDTO[]; }>({});
  const [loadingChildTasks, setLoadingChildTasks] = useState<number[]>([]);
  const [addingChildForSubTask, setAddingChildForSubTask] = useState<number | null>(null);
  const [editingChildId, setEditingChildId] = useState<number | null>(null);
  const [tasks, setTasks] = useState<TaskDTO[]>([]);
  //#region Department wise filter
  const [departmentFilter, setDepartmentFilter] = useState(department?.toLowerCase() || "all");
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [statusDropdownValue, setStatusDropdownValue] = useState("all");
  const [overallLogs, setOverallLogs] = useState<OverallLogDTO[]>([]);
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [selectedLogEntity, setSelectedLogEntity] = useState<{ type: "task" | "subtask" | "childSubTask"; id: number; title?: string; } | null>(null);
  const [showPercentCol, setShowPercentCol] = useState(true);
  const [showCompletedTasks, setShowCompletedTasks] = useState(true);
  const [showReleasedTasks, setShowReleasedTasks] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState<{ [key: string]: number }>({});
  const [totalCommentCount, setTotalCommentCount] = useState<{ [key: string]: number }>({});
  const [showCompletedReleased, setShowCompletedReleased] = useState(true);
  const [showNotInScope, setshowNotInScope] = useState(false);
  const [showHours, setshowHours] = useState(true);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [useWeekdays, setUseWeekdays] = useState(false);
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedTaskForTime, setSelectedTaskForTime] = useState<TaskDTO | null>(null);
  const [selectedSubTaskForTime, setSelectedSubTaskForTime] = useState<SubTaskDTO | null>(null);
  const [selectedChildSubTaskForTime, setSelectedChildSubTaskForTime] = useState<ChildSubTask | null>(null);
  const [showOnDays, setShowOnDays] = useState(true);
  const [showOnpercetbar, setShowOnbar] = useState(true);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null); // MODIFIED: State for current user
  const [childSpentHours, setChildSpentHours] = useState<number>(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; subTaskId: number; } | null>(null);
  const [deleteSubTaskId, setDeleteSubTaskId] = useState<number | null>(null);
  const [deleteSubTaskTaskId, setDeleteSubTaskTaskId] = useState<number | null>(null);
  const [showDeleteSubTaskModal, setShowDeleteSubTaskModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<any>({});
  const [selectedUserFilter, setSelectedUserFilter] = useState("all");



  // 🔥 Spent Hours Modal
  const [spentModalOpen, setSpentModalOpen] = useState(false);
  const [spentLogs, setSpentLogs] = useState<OverallLogDTO[]>([]);
  const [spentEntityTitle, setSpentEntityTitle] = useState("");
  // const canCreate = userID  === "";

  const [tooltipOpen, setTooltipOpen] = useState(false);

  const toggletip = () => setTooltipOpen(!tooltipOpen);

  const [canCreate, setCanCreate] = useState(false);

  useEffect(() => {
    getCanCreate().then(setCanCreate);
  }, []);

  const hiddenUsers = ["Gokul", "Muthu", "Krish", "Aishwarya Jahagirdar", "Limya", "Admin", "-"];




  //#region Client Input
  // 🔹 Client Input Modal state
  const [inputModalOpen, setInputModalOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<{ type: "task" | "subtask" | "childSubTask"; id: number; } | null>(null);

  //#endregion  

  const loadSpentHoursLogs = async (type: "task" | "subtask" | "childSubTask", id: number, title?: string) => {
    try {
      let logs: OverallLogDTO[] = [];

      if (type === "task") {
        logs = await getTaskLogs(id);
      }
      else if (type === "subtask") {
        logs = await getSubTaskLogs(id);
      }
      else if (type === "childSubTask") {
        logs = await getChildSubTaskLogs(id); // ✅ NEW
      }

      // 🔥 ONLY SpentHours changes
      const filtered = logs.filter(
        l => l.fieldName === "SpentHours"
      );

      setSpentLogs(filtered);
      setSpentEntityTitle(title || "");
      setSpentModalOpen(true);

    } catch (err) {
      console.error("Spent hours log error", err);
      setSpentLogs([]);
      setSpentModalOpen(true);
    }
  };


  const userHourMap = spentLogs.reduce<Record<string, number>>(
    (acc, log) => {
      const oldVal = Number(log.oldValue ?? 0);
      const newVal = Number(log.newValue ?? 0);
      const diff = newVal - oldVal;

      if (!acc[log.userName]) acc[log.userName] = 0;
      acc[log.userName] += diff;

      return acc;
    },
    {}
  );

  const totalWorked = Object.values(userHourMap).reduce(
    (a, b) => a + b,
    0
  );


  const toggleAddModal = () => setAddModalOpen(!addModalOpen);
  const isEditMode =
    inlineEditTaskId !== null ||
    inlineEditSubTaskId !== null ||
    showInlineCreateTask ||
    Object.values(inlineNewSubtaskTaskId).some(Boolean);

  const openDeleteModal = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteId(null);
    setShowDeleteModal(false);
  };


  //#region Status Filter

  const allStatuses = [
    { value: "YetToStart", label: "Yet To Start" },
    { value: "WaitingForInput", label: "Waiting For Input" },
    { value: "Cancelled", label: "Cancelled" },
    { value: "OnHold", label: "On Hold" },
    { value: "Issues", label: "Issues" },
    { value: "Released", label: "Released" },
    { value: "ModellingTodo", label: "Modelling Todo" },
    { value: "DraftingTodo", label: "Drafting Todo" },
    { value: "QCTodo", label: "QC Todo" },
    { value: "RevisionTodo", label: "Revision Todo" },
    { value: "ClientReviewTodo", label: "Client Review Todo" },
    { value: "FinalCorrectionTodo", label: "Final Correction Todo" },
    { value: "ModellingInProgress", label: "Modelling In Progress" },
    { value: "DraftingInProgress", label: "Drafting In Progress" },
    { value: "QCInProgress", label: "QC In Progress" },
    { value: "RevisionInProgress", label: "Revision In Progress" },
    { value: "ClientReviewInProgress", label: "Client Review In Progress" },
    { value: "FinalCorrectionInProgress", label: "Final Correction In Progress" },
    { value: "ModellingCompleted", label: "Modelling Completed" },
    { value: "DraftingCompleted", label: "Drafting Completed" },
    { value: "QCCompleted", label: "QC Completed" },
    { value: "RevisionCompleted", label: "Revision Completed" },
    { value: "ClientReviewCompleted", label: "Client Review Completed" },
    { value: "FinalCorrectionCompleted", label: "Final Correction Completed" },
    { value: "NotInScope", label: "Not In Scope" },

    // 🔥 Changed only display label
    { value: "Completed", label: "Ready for Release" },
  ];


  const inProgressStatuses = [
    "ModellingInProgress",
    "DraftingInProgress",
    "QCInProgress",
    "RevisionInProgress",
    "ClientReviewInProgress",
    "FinalCorrectionInProgress"
  ];

  const completedStatuses = [
    "ModellingCompleted",
    "DraftingCompleted",
    "QCCompleted",
    "RevisionCompleted",
    "ClientReviewCompleted",
    "FinalCorrectionCompleted",
  ];

  const ToDoStatuses = [
    "ModellingTodo",
    "DraftingTodo",
    "QCTodo",
    "RevisionTodo",
    "ClientReviewTodo",
    "FinalCorrectionTodo"
  ];

  //#region worksheet

  const hourOptions = [0.25, 0.5, 0.75, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12];

  const prefixOptions = [
    'Modeling',
    'QC Modeling',
    'Drafting',
    'Drafting Update',
    'QC Drafting',
    'Design',
    'QC Design',
    'CQP/CP',
    'CP & Release',
    'Training',
    'Teamcentre',
    'Client Meeting',
    'Management',
    'ISO',
    'Others'
  ];

  const drawingTypeOptions = [
    { code: "6MM", label: "6MM", full: "Standard 6 mm thickness part drawing" },

    { code: "2SG", label: "2SG", full: "2 Subgroup assembly drawing " },

    { code: "3EA", label: "3EA", full: "3 Element assembly (Extended) " },

    { code: "4MA", label: "4MA", full: "4 Member assembly drawing " },

    { code: "8PP", label: "8PP", full: "8 Part package drawing " },

    { code: "COMP-WELD", label: "COMP-WELD", full: "Complicated weldments drawing " },

    { code: "1FA", label: "1FA" },

    { code: "Others", label: "Other" },
  ];

  const modellingOptions = [
    "Modelling",
    "Modelling Update",
    "Modelling Review"
  ];

  const draftingOptions = [
    "Drafting",
    "Drafting Update",
    "Drafting Review"
  ];

  const otherOptions = [
    "Others",
    "CP request",
    "Release",
    "Training",
    "Teamcentre",
    "Client Meeting",
    "Management",
    "ISO"
  ];


  const { data: projectNames = [] } = useQuery<string[]>({
    queryKey: ['projectNames'],
    queryFn: getProjectNames,
  });

  useEffect(() => {
    if (!selectedTaskForTime) return;

    setNewRowData(prev => ({
      ...prev,
      taskId: selectedTaskForTime.id,
      taskDescription: selectedTaskForTime.description,
      projectName: selectedTaskForTime.projectName,
      createdByName: selectedTaskForTime?.createdByName,

    }));
  }, [selectedTaskForTime]);


  useEffect(() => {
    setNewRowData(prev => ({
      ...prev,
      subTaskId: selectedSubTaskForTime?.id,
      subTaskDescription: selectedSubTaskForTime?.description,
      createdByName: selectedSubTaskForTime?.createdByName,
    }));
  }, [selectedSubTaskForTime]);

  useEffect(() => {
    if (!selectedChildSubTaskForTime) return;

    setNewRowData(prev => ({
      ...prev,
      childSubTaskDescription: selectedChildSubTaskForTime.description
    }));
  }, [selectedChildSubTaskForTime]);


  useEffect(() => {
    if (!userID) return;

    const loggedInUser = {
      id: userID,
      name: name || ""   // ✅ SAFE, NOT GLOBAL
    };

    setCurrentUser(loggedInUser);
  }, [userID, name]);

  const getTaskTypeOptions = (childName?: string) => {
    if (!childName) return [];

    const text = childName.toLowerCase();

    if (text.includes("model")) return modellingOptions;
    if (text.includes("draft")) return draftingOptions;

    return otherOptions;
  };


  const mutation = useMutation({
    mutationFn: (dto: CreateTimeSheetDTO | { timeId: string; dto: CreateTimeSheetDTO }) => {
      if ('timeId' in dto) return updateTimeSheet(dto.timeId, dto.dto).then((res) => res.data);
      return createTimeSheet(dto as CreateTimeSheetDTO).then((res) => res.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
      setNewRowData({});
      toast.success('Timesheet saved successfully!');
    },
    onError: (error: any) => {
      console.error('Full error object:', error);

      const responseData = error?.response?.data;
      console.log('Backend error response:', responseData);

      const message =
        responseData?.message ||
        responseData?.error ||
        error?.message ||
        'Something went wrong. Please try again.';

      toast.error(message);
    },


  });


  const saveRow = async (rowId: string) => {
    if (!newRowData.projectName || !newRowData.hours || !newRowData.createdByName) {
      toast.error("Please fill all required fields.");
      return;
    }

    const dto: CreateTimeSheetDTO = {
      date: new Date().toISOString().split("T")[0],
      projectName: newRowData.projectName,
      hours: newRowData.hours,
      assignedBy: newRowData.createdByName,
      description: newRowData.description ?? null, // remarks only
      module: newRowData.taskDescription,
      product: newRowData.product,
      subTask: newRowData.subTaskDescription ?? null,
      childSubTask: newRowData.childSubTaskDescription ?? null,
      part: newRowData.part,
      draftingType: newRowData.draftingType ?? null,
      quantity: newRowData.quantity ?? null,
      totPages: newRowData.totPages ?? null,
    };


    try {
      // 1️⃣ Save timesheet
      if (rowId === "new") {
        await mutation.mutateAsync(dto);
      } else {
        await mutation.mutateAsync({ timeId: rowId, dto });
      }

      // 2️⃣ 🔥 UPDATE SPENT HOURS
      await addSpentHours(newRowData.hours);

      // 3️⃣ Refresh tasks
      if (selectedProjectId) {
        await fetchProjectTasks(selectedProjectId);
      }

      // 4️⃣ Cleanup
      toggleAddModal();
      setNewRowData({});
      setSelectedTaskForTime(null);
      setSelectedSubTaskForTime(null);

    } catch (err) {
      console.error(err);
    }
  };





  const addSpentHours = async (hours: number) => {

    if (selectedChildSubTaskForTime) {
      const updatedSpent =
        (selectedChildSubTaskForTime.spentHours ?? 0) + hours;

      // 1️⃣ Backend update
      await updateMyChildSubTask(selectedChildSubTaskForTime.id, {
        spentHours: updatedSpent,
      });

      const subTaskId = selectedChildSubTaskForTime.subTaskId;

      // ✅ Guard: subTaskId must exist
      if (typeof subTaskId !== "number") {
        console.warn("Child subtask missing subTaskId");
        return;
      }

      // 2️⃣ Update UI state
      setChildTasks(prev => {
        const existing = prev[subTaskId];

        if (!existing) return prev;

        return {
          ...prev,
          [subTaskId]: existing.map((ct: ChildSubTaskViewDTO) =>
            ct.id === selectedChildSubTaskForTime.id
              ? { ...ct, spentHours: updatedSpent }
              : ct
          ),
        };
      });

      // 3️⃣ Update selected object
      setSelectedChildSubTaskForTime(prev =>
        prev ? { ...prev, spentHours: updatedSpent } : prev
      );

      return;
    }

    // SUBTASK
    if (selectedSubTaskForTime) {
      const updatedSpent =
        (selectedSubTaskForTime.spentHours ?? 0) + hours;

      await updateMySubTask(selectedSubTaskForTime.id, {
        spentHours: updatedSpent,
      });

      return;
    }

    // TASK
    if (selectedTaskForTime) {
      const updatedSpent =
        (selectedTaskForTime.spentHours ?? 0) + hours;

      await updateMyTask(selectedTaskForTime.id, {
        spentHours: updatedSpent,
      });
    }
  };







  //#region comments

  const [commentPopup, setCommentPopup] = useState<{
    open: boolean;
    type: "project" | "task" | "subtask" | "childSubTask";
    id: number;
    title?: string;
  } | null>(null);

  const openComments = (
    type: "project" | "task" | "subtask" | "childSubTask",
    id: number,
    title?: string
  ) => {
    setCommentPopup({
      open: true,
      type,
      id,
      title
    });
  };


  const loadTotalCommentsFor = async (type: "task" | "subtask", id: number) => {
    try {
      const comments = await getComments(type, id);
      setTotalCommentCount(prev => {
        const updated = { ...prev, [`${type}-${id}`]: comments.length };
        localStorage.setItem("totalCommentCount", JSON.stringify(updated)); // 🔥 Save permanently
        return updated;
      });
    } catch {
      setTotalCommentCount(prev => {
        const updated = { ...prev, [`${type}-${id}`]: 0 };
        localStorage.setItem("totalCommentCount", JSON.stringify(updated));
        return updated;
      });
    }
  };

  async function loadLogCount(type: "task" | "subtask" | "childSubTask", id: number) {
    try {
      const logs =
        type === "task"
          ? await getTaskAuditLogs(id)
          : await getSubTaskAuditLogs(id);

      return Array.isArray(logs) ? logs.length : 0;
    } catch (err) {
      console.error("Error getting logs:", err);
      return 0;
    }
  }

  const loadUnreadCountFor = async (type: "task" | "subtask" | "childSubTask", id: number) => {
    try {
      const key = `${type}-${id}`;

      // 🔥 If unread exists already in localStorage, do NOT override it from API
      if (unreadCounts[key] > 0) return;

      const count = await getUnreadCount(type, id);

      setUnreadCounts(prev => {
        const updated = { ...prev, [key]: count };
        localStorage.setItem("unreadCounts", JSON.stringify(updated));
        return updated;
      });

    } catch {
      setUnreadCounts(prev => {
        const updated = { ...prev, [`${type}-${id}`]: prev[`${type}-${id}`] ?? 0 };
        localStorage.setItem("unreadCounts", JSON.stringify(updated));
        return updated;
      });
    }
  };


  //#region ChildSubtask
  const expandSubTask = async (subTaskId: number) => {
    setExpandedSubTasks(prev => ({ ...prev, [subTaskId]: true }));

    if (childTasks[subTaskId]) return;

    try {
      setLoadingChildTasks(prev => [...prev, subTaskId]);

      const res = await getChildSubTaskById(subTaskId);

      const data = Array.isArray(res.data) ? res.data : [res.data];

      setChildTasks(prev => ({
        ...prev,
        [subTaskId]: data
      }));

    } finally {
      setLoadingChildTasks(prev => prev.filter(id => id !== subTaskId));
    }
  };

  const collapseSubTask = (subTaskId: number) => {
    setExpandedSubTasks(prev => ({ ...prev, [subTaskId]: false }));
  };


  //#endregion

  const calculatePercent = (spent?: number, max?: number) => {
    if (!max || max === 0) return 0;
    return Math.min(100, Math.round(((spent ?? 0) / max) * 100));
  };


  //#region Hours 
  const toDisplayValue = (hours: number) =>
    showOnDays
      ? Math.round(hours)
      : Math.round(hours / 9);

  const unitLabel = showOnDays ? " H" : " ";


  // #region visible columns
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    "number",
    "description",
    "Remaining",
    "assigned",
    "status",
    "dueDate",
    "completion",
    "priority",
    "percent",
    "comment",
    "log",
    "actions",
    "hours"
  ]);




  const [columnsOrder, setColumnsOrder] = React.useState([
    { key: "number", label: "#", width: "6%" },
    { key: "description", label: "Description", width: "18%" },
    { key: "maxHours", label: "Max", width: "4%" },
    { key: "spentHours", label: "Spent", width: "4%" },
    { key: "hours", label: "Time ", width: "7%" },
    { key: "percent", label: "Work Done ", width: "7%" },
    { key: "assigned", label: "Assigned", width: "9%" },
    { key: "status", label: "Status", width: "11%" },
    { key: "priority", label: "Priority", width: "6%" },
    { key: "dueDate", label: "Due Date", width: "9%" },
    { key: "completion", label: "Due", width: "9%" },
    { key: "comment", label: "Chat", width: "10%" },// NEW Comment column%
    { key: "log", label: "Log", width: "6%" },// NEW Comment column
    { key: "actions", label: "Edit", width: "6%" }
  ]);

  const hiddenInEditMode = ["completion", "comment", "log", "maxHours", "Remaining", "hours", "spentHours", "dueDate", "actions"];

  const effectiveColumns = isEditMode
    ? columnsOrder.filter(col => !hiddenInEditMode.includes(col.key))
    : columnsOrder;

  //#region Logs

  const loadOverallLogs = async (entityType: "task" | "subtask" | "childSubTask", entityId: number) => {
    try {
      let logs: OverallLogDTO[] = [];

      if (entityType === "task") {
        logs = await getTaskLogs(entityId);
      }
      else if (entityType === "subtask") {
        logs = await getSubTaskLogs(entityId);
      }
      else if (entityType === "childSubTask") {
        logs = await getChildSubTaskLogs(entityId); // NEW
      }


      setOverallLogs(logs);
      setLogModalOpen(true);
    } catch (err) {
      console.error("Failed to load overall logs:", err);
      setOverallLogs([]);
    }
  };


  //#region Filter


  const formatStatusLabel = (status: string) => {
    const overrides: Record<string, string> = {
      Completed: "Ready for Release",
      YetToStart: "Yet to Start",
      NotApplicable: "Not In Scope",
    };

    if (overrides[status]) {
      return overrides[status];
    }

    return status
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
      .replace(/And/g, "&")
      .trim();
  };


  const childformatStatusLabel = (status: string) => {
    const overrides1: Record<string, string> = {
      Completed: "Completed",
      YetToStart: "Yet to Start",
      NotApplicable: "Not In Scope",
      NotApplicable1: "Released",
      CompletedAndReleased: "Completed",
    };

    if (overrides1[status]) {
      return overrides1[status];
    }

    return status
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
      .replace(/And/g, "&")
      .trim();
  };

  const getProjectOverallStatus = (tasks: TaskDTO[]): string => {
    const finishedStatuses = ['Completed', 'Released'];

    if (!tasks || tasks.length === 0) {
      return 'InProgress';
    }

    const allItemsAreFinished = tasks.every(task => {
      const taskIsFinished = finishedStatuses.includes(task.status);
      const subtasksAreFinished = (task.subTasks ?? []).every(subtask =>
        finishedStatuses.includes(subtask.status)
      );
      return taskIsFinished && subtasksAreFinished;
    });

    return allItemsAreFinished ? 'Completed' : 'InProgress';
  };

  // === Filtering and task list preparation ===



  // 1️⃣ The selected project
  const selectedProject = projects.find(p => p.id === selectedProjectId);
  const allTasksForProject = selectedProject?.tasks || [];



  // 2️⃣ Toggle-based tasks (you already have this)
  const tasksToShowBasedOnToggle = allTasksForProject;

  // 3️⃣ Department filter
  const departmentFilteredTasks = tasksToShowBasedOnToggle.filter(task => {
    if (departmentFilter === "all") return true;

    return (
      task.createdByDepartment?.toLowerCase().trim() ===
      departmentFilter.toLowerCase().trim()
    );
  });

  // 4️⃣ Start with department-filtered tasks
  let filteredTasks = departmentFilteredTasks.slice();
  // USER FILTER
  // USER FILTER
  if (selectedUserFilter !== "all") {

    const expandedTaskState: Record<number, boolean> = {};
    const expandedSubtaskState: Record<number, boolean> = {};

    filteredTasks = filteredTasks
      .map(task => {

        // MAIN TASK MATCH
        const taskMatch =
          task.assignedToId === selectedUserFilter;

        // MATCHED SUBTASKS
        const matchedSubTasks =
          (task.subTasks || []).filter(sub => {

            // SUBTASK USER MATCH
            const subTaskMatch =
              sub.assignedToId === selectedUserFilter;

            // CHILD SUBTASK USER MATCH
            const childMatch =
              (childTasks[sub.id] || []).some(
                child =>
                  child.teamMemberIds?.includes(selectedUserFilter)
              );

            // AUTO EXPAND SUBTASK
            if (subTaskMatch || childMatch) {
              expandedSubtaskState[sub.id] = true;
            }

            return subTaskMatch || childMatch;
          });

        // AUTO EXPAND TASK
        if (taskMatch || matchedSubTasks.length > 0) {
          expandedTaskState[task.id] = true;

          return {
            ...task,
            subTasks: taskMatch
              ? task.subTasks
              : matchedSubTasks
          };
        }

        return null;
      })
      .filter(Boolean) as TaskDTO[];

  }


  // -----------------------------------------------------
  // 5️⃣ STATUS DROPDOWN FILTER — works on Task OR Subtask
  // -----------------------------------------------------
  if (statusDropdownValue !== "all") {
    filteredTasks = filteredTasks
      .map(task => {
        const taskMatches = task.status === statusDropdownValue;

        const matchingSubtasks = (task.subTasks || []).filter(
          st => st.status === statusDropdownValue
        );

        if (taskMatches || matchingSubtasks.length > 0) {
          return {
            ...task,
            // show ONLY matching subtasks OR all if task matched
            subTasks: taskMatches ? task.subTasks : matchingSubtasks
          };
        }
        return null;
      })
      .filter(Boolean) as TaskDTO[];
  }


  // -----------------------------------------------------
  // 6️⃣ STATUS MODAL MULTI-SELECT FILTER
  // -----------------------------------------------------
  if (selectedStatuses.length > 0) {
    filteredTasks = filteredTasks
      .map(task => {
        const taskMatches = selectedStatuses.includes(task.status);

        const matchingSubtasks = (task.subTasks || []).filter(st =>
          selectedStatuses.includes(st.status)
        );

        if (taskMatches || matchingSubtasks.length > 0) {
          return {
            ...task,
            subTasks: taskMatches ? task.subTasks : matchingSubtasks
          };
        }

        return null;
      })
      .filter(Boolean) as TaskDTO[];
  }

  useEffect(() => {
    // Search cleared → return normal view
    if (!searchTerm.trim()) {
      setShowSubtasksForTask({});
      return;
    }

    const expanded: Record<number, boolean> = {};

    filteredTasks.forEach(task => {
      if (task.subTasks?.length > 0) {
        expanded[task.id] = true;
      }
    });

    setShowSubtasksForTask(expanded);

  }, [searchTerm]);


  // -----------------------------------------------------
  // 7️⃣ SEARCH FILTER (your original logic)
  // -----------------------------------------------------
  filteredTasks = filteredTasks.filter(task => {
    if (!searchTerm.trim()) return true;
    const lower = searchTerm.toLowerCase();

    filteredTasks = filteredTasks
      .map((task) => {

        // Task description match
        const taskMatch =
          task.description?.toLowerCase().includes(lower);

        // Matching subtasks only
        const matchedSubTasks =
          (task.subTasks || []).filter((subtask) =>
            subtask.description?.toLowerCase().includes(lower)
          );

        // If task matches → show task with all subtasks
        if (taskMatch) {
          return task;
        }

        // If subtask matches → show only matching subtask
        if (matchedSubTasks.length > 0) {
          return {
            ...task,
            subTasks: matchedSubTasks
          };
        }

        return null;
      })
      .filter(Boolean) as TaskDTO[];

    const assignedMember = teamMembers.find(m => m.userID === task.assignedToId);
    const assigneeName = assignedMember ? assignedMember.name.toLowerCase() : "";

    const descriptionMatch = task.description?.toLowerCase().includes(lower);
    const ownerMatch = task.createdByName?.toLowerCase().includes(lower);
    const statusMatch = task.status?.toLowerCase().includes(lower);
    const priorityMatch = task.priority?.toLowerCase().includes(lower);

    const dueDateMatch = task.dueDate
      ? new Date(task.dueDate)
        .toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
        .toLowerCase()
        .includes(lower)
      : false;

    const completionDateMatch = task.completionDate
      ? new Date(task.completionDate)
        .toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
        .toLowerCase()
        .includes(lower)
      : false;

    return (
      assigneeName.includes(lower) ||
      descriptionMatch ||
      ownerMatch ||
      statusMatch ||
      priorityMatch ||
      dueDateMatch ||
      completionDateMatch
    );
  });


  //#region ShowMyTask

  if (showMyTasksOnly) {
    filteredTasks = filteredTasks
      .map(task => {
        const isMyTask = task.assignedToId === userID;

        const mySubtasks = task.subTasks?.filter(st => st.assignedToId === userID) || [];

        if (isMyTask || mySubtasks.length > 0) {
          return {
            ...task,
            subTasks: isMyTask ? task.subTasks : mySubtasks
          };
        }

        return null;
      })
      .filter(Boolean) as TaskDTO[];
  }

  //#region Drag and Drop
  const [modalPos, setModalPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const dragColIndex = useRef<number | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((prev) => !prev);



  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  });



  const DEFAULT_DUE_COLORS = {
    greaterThan10: "#7a7a7a",  // green
    lessThan10: "#000000",     // yellow
    lessThan5: "#0061ff",      // orange
    overdue: "#f9001a",        // red
    onTime: "#669c35",         // blue
    completed: "#ffffff",      // grey
  };

  const [dueColorSettings, setDueColorSettings] = useState(() => {
    const saved = localStorage.getItem("dueColorSettings");
    return saved ? JSON.parse(saved) : DEFAULT_DUE_COLORS;
  });

  const getDueColor = (diff: number, isCompleted: boolean) => {
    if (isCompleted) return dueColorSettings.completed;

    if (diff < 0) return dueColorSettings.overdue;
    if (diff <= 5) return dueColorSettings.lessThan5;
    if (diff <= 10) return dueColorSettings.lessThan10;

    return dueColorSettings.greaterThan10;
  };


  const handleMouseDown = (e: any) => {
    setDragging(true);
    setOffset({
      x: e.clientX - modalPos.x,
      y: e.clientY - modalPos.y,
    });
  };

  const handleMouseMove = (e: any) => {
    if (!dragging) return;
    setModalPos({
      x: e.clientX - offset.x,
      y: e.clientY - offset.y,
    });
  };



  const handleMouseUp = () => setDragging(false);


  const handleDragStart = (e: React.DragEvent<HTMLTableHeaderCellElement>, index: number) => {
    dragColIndex.current = index;
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e: React.DragEvent<HTMLTableHeaderCellElement>) => {
    e.preventDefault();
  };
  const handleDrop = (e: React.DragEvent<HTMLTableHeaderCellElement>, dropIndex: number) => {
    e.preventDefault();
    if (dragColIndex.current === null) return;
    const tempCols = [...columnsOrder];
    const [moved] = tempCols.splice(dragColIndex.current, 1);
    tempCols.splice(dropIndex, 0, moved);
    setColumnsOrder(tempCols);
    dragColIndex.current = null;
  };

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Sort tasks if requested
  let sortedTasks = [...filteredTasks];
  if (sortConfig.key) {
    sortedTasks.sort((a: any, b: any) => {
      let aValue: any = a[sortConfig.key as keyof typeof a];
      let bValue: any = b[sortConfig.key as keyof typeof b];

      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      if (typeof sortConfig.key === 'string' && sortConfig.key.toLowerCase().includes('date')) {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }


  //#endregion





  // add inside your component
  const statusPercentMap: Record<string, number> = {
    ModellingCompleted: 30,
    DraftingCompleted: 40,
    QCCompleted: 50,
    RevisionCompleted: 60,
    ClientReviewCompleted: 70,
    FinalCorrectionCompleted: 80,
    Released: 100,
    Completed: 100
  };


  //#region fetchProjects

  useEffect(() => {
    if (role !== 'TeamLeader') return;
    (async () => {
      await fetchProjects();
      await fetchTeamMembers();
    })();
  }, [role]);

  async function fetchTeamMembers() {
    try {
      const res = await getTeammember();
      const data = res.data?.$values || res.data || [];
      setTeamMembers(data);
    } catch { }
  }

  async function fetchProjects() {
    setLoadingProjects(true);
    setProjectError(null);
    try {
      const res = await getTeamLeadProjects();
      const data = res.data?.$values || res.data || [];
      const projectsList = data.map((p: any) => ({ ...p, id: Number(p.id) }));
      setProjects(projectsList);
      setDepartmentFilter(department?.toLowerCase() || "all");
      return projectsList;
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to load projects';
      setProjectError(message);
      toast.error(message);
      return [];
    } finally {
      setLoadingProjects(false);
    }
  }


  //#region Hide columns

  useEffect(() => {
    localStorage.clear();
    const savedColumns = localStorage.getItem("visibleColumns");
    if (savedColumns) setVisibleColumns(JSON.parse(savedColumns));

    const saved = localStorage.getItem("showCompletedReleased");
    if (saved) setShowCompletedReleased(saved === "true");

    const savednot = localStorage.getItem("showNotInScope");
    if (savednot) setshowNotInScope(savednot === "true");

    const savedWeekdays = localStorage.getItem("useWeekdays");
    if (savedWeekdays) setUseWeekdays(savedWeekdays === "true");

    const savedShowOnDays = localStorage.getItem("showOnDays");
    if (savedShowOnDays) setShowOnDays(savedShowOnDays === "true");

    const savedColumnss = localStorage.getItem("visibleColumns");
    if (savedColumns) setVisibleColumns(JSON.parse(savedColumns));



  }, []); // <--- MUST HAVE THIS (prevents infinite rendering)

  useEffect(() => {
    const savedUnread = localStorage.getItem("unreadCounts");
    if (savedUnread) setUnreadCounts(JSON.parse(savedUnread));
  }, []); // Only once!


  //#region fetchProjectTasks

  async function fetchProjectTasks(projectId: number) {


    setLoadingTasks(true);
    setTasksError(null);
    try {
      const project = projects.find(p => p.id === projectId);
      if (!project) throw new Error('Project not found');
      const res = await getTLProjectDetails(project.projectID);
      if (!res.data) throw new Error('No data received');
      const tasksArray = Array.isArray(res.data.tasks) ? res.data.tasks : res.data.tasks?.$values || [];
      const rawTasks: TaskDTO[] = tasksArray.map((task: any) => ({
        ...task,
        description: task.description || task.title || '',

        issues: task.issues || '',
        totalLogs: Array.isArray(task.logs)
          ? task.logs.length
          : task.logs?.$values?.length ?? 0,
        spentHours: task.spentHours ?? task.SpentHours ?? 0,
        maxHours: task.maxHours ?? task.MaxHours ?? 0,
        subTasks: (Array.isArray(task.subTasks) ? task.subTasks : task.subTasks?.$values || []).map((sub: any) => ({
          ...sub,
          taskId: task.id,
          description: sub.description || sub.title || '',
          totalLogs: Array.isArray(sub.logs)
            ? sub.logs.length
            : sub.logs?.$values?.length ?? 0,
          spentHours: sub.spentHours ?? sub.SpentHours ?? 0,
          maxHours: sub.maxHours ?? sub.MaxHours ?? 0,
        }))
      }));




      // 🔥 ADD THIS
      const tasksData = rawTasks;


      setProjects(prev =>
        prev.map(p => (p.id === projectId ? { ...p, tasks: tasksData } : p))
      );
      // await preloadAllChildSubtasks(tasksData);
      tasksData.forEach((task) => {
        loadUnreadCountFor("task", task.id);
        loadTotalCommentsFor("task", task.id);

        task.subTasks.forEach((sub) => {
          loadUnreadCountFor("subtask", sub.id);
          loadTotalCommentsFor("subtask", sub.id);

        });
      });
      setShowSubtasksForTask({});
      setInlineNewTaskProjectId(projectId);
    } catch (error: any) {
      console.error('Error fetching project details:', error);
      const message = error.response?.data?.message || 'Error loading tasks for this project';
      setTasksError(message);
      toast.error(message);
    } finally {
      setLoadingTasks(false);
    }
  }

  function toggleSubtasksDropdown(taskId: number) {
    setShowSubtasksForTask(prev => {
      const isCurrentlyOpen = prev[taskId];
      return { ...prev, [taskId]: !isCurrentlyOpen };
    });

    setInlineNewSubtaskTaskId(prevInline => ({
      ...prevInline,
      [taskId]: false,
    }));
  }

  async function refreshTasksList() {
    if (!selectedProjectId) return;
    await fetchProjectTasks(selectedProjectId);
  }
  function openInlineEditTask(taskId: number) {
    setInlineEditTaskId(taskId);
    setInlineEditSubTaskId(null);
    setInlineNewSubtaskTaskId({});
  }
  function closeInlineEditTask(refresh = false) {
    setInlineEditTaskId(null);
    if (refresh) {
      refreshTasksList();
    }
  }
  function openInlineEditSubTask(subTaskId: number, taskId: number) {
    setInlineEditSubTaskId(subTaskId);
    setInlineEditTaskId(null);
    setInlineNewSubtaskTaskId({});
    setShowSubtasksForTask(prev => ({ ...prev, [taskId]: true }));
  }
  async function closeInlineEditSubTask(refresh = false, taskId?: number) {
    setInlineEditSubTaskId(null);

    if (refresh && taskId) {
      await refreshTasksList();
      setShowSubtasksForTask({ [taskId]: true });
    }
  }
  function openInlineCreateSubTask(taskOrSubtaskId: number) {
    setInlineNewSubtaskTaskId(prev => ({ ...prev, [taskOrSubtaskId]: true }));
    setInlineEditTaskId(null);
    setInlineEditSubTaskId(null);
    setShowSubtasksForTask(prev => ({ ...prev, [taskOrSubtaskId]: true }));
  }
  async function closeInlineCreateSubTask(taskOrSubtaskId: number, refresh = false) {
    setInlineNewSubtaskTaskId(prev => ({ ...prev, [taskOrSubtaskId]: false }));
    if (refresh) {
      refreshTasksList();
      setInlineNewSubtaskTaskId(prev => ({ ...prev, [taskOrSubtaskId]: true }));
    }
  }

  function openInlineCreateChildSubTask(subTaskId: number) {
    setInlineNewChildSubTaskId(prev => ({
      ...prev,
      [subTaskId]: true,
    }));
    setExpandedSubTasks(prev => ({
      ...prev,
      [subTaskId]: true,
    }));
  }
  function closeInlineCreateChildSubTask(subTaskId: number, refresh = false) {
    setInlineNewChildSubTaskId(prev => ({
      ...prev,
      [subTaskId]: false,
    }));

    if (refresh) {
      setChildTasks(prev => {
        const copy = { ...prev };
        delete copy[subTaskId];   // force reload
        return copy;
      });

      expandSubTask(subTaskId);  // 🔥 ALWAYS expand, never toggle
    }
  }




  const handleViewMore = (remarks: string) => {
    setSelectedRemarks(remarks);
    setRemarksModalOpen(true);
  };
  const openInlineCreateTask = () => setShowInlineCreateTask(true);
  const closeInlineCreateTask = () => setShowInlineCreateTask(false);

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleProjectChange = async (event: React.ChangeEvent<any>) => {
    const selectedValue = event.target.value;

    setInlineEditTaskId(null);
    setInlineEditSubTaskId(null);

    if (selectedValue === 'select' || selectedValue === '') {
      setSelectedProjectId(null);
      setProjects(prev => prev.map(p => ({ ...p, tasks: [] })));
      setTasksError(null);
      return;
    }

    const projectId = parseInt(selectedValue, 10);
    if (!isNaN(projectId)) {
      setSelectedProjectId(projectId);
      await fetchProjectTasks(projectId);
    }
  };

  if (role !== 'TeamLeader' && role !== 'Admin') {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Alert color="danger">Only Team Leaders can access this page</Alert>
      </Container>
    );
  }


  //#region colors

  const getDueDateColor = (dateString: string | null | undefined, status: string): string => {
    if (!dateString) return 'secondary';
    if (status === 'Completed' || status === 'Released') return 'success';

    const dueDate = new Date(dateString);
    const today = new Date();
    dueDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return 'danger';
    }

    if (diffDays <= 2) {
      return 'warning';
    }

    return 'success';
  };
  const getDueDateMessage = (dateString: string | null | undefined, status: string): string | null => {
    if (!dateString || status === 'Completed' || status === 'Released') return null;
    const dueDate = new Date(dateString);
    const today = new Date();
    dueDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return `This project is overdue by ${Math.abs(diffDays)} day(s).`;
    }
    if (diffDays === 0) {
      return "This project is due today!";
    }
    if (diffDays <= 2) {
      return `This project is due in ${diffDays} day(s).`;
    }

    return null;
  };


  // Function to count only working days (Mon-Fri)


  //#region Delete Task

  const handleDeleteTask = async () => {
    if (!deleteId) return;

    try {
      await deleteTask(deleteId);

      // Remove from UI instantly
      setTasks((prev: any[]) => prev.filter((t: any) => t.id !== deleteId));

      // 🔥 Auto refresh task list from backend (PREVENT stale subtasks)
      await refreshTasksList();

      toast.success("Task & Subtasks Deleted Successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to Delete Task!");
    }

    closeDeleteModal();
  };

  const handleDeleteChildSubTask = async (id: number, subTaskId: number) => {

    try {
      await deleteChildSubTask(id);

      setChildTasks(prev => {
        const updated = { ...prev };

        if (!updated[subTaskId]) return prev;

        updated[subTaskId] = updated[subTaskId].filter(ct => ct.id !== id);

        return updated;
      });

      alert("Child subtask deleted successfully");
    } catch (error) {
      console.error("Delete failed", error);
      alert("Failed to delete child subtask");
    }
  };

  const confirmDeleteChildSubTask = async () => {
    if (!deleteTarget) return;

    const { id, subTaskId } = deleteTarget;

    try {
      await deleteChildSubTask(id);

      setChildTasks(prev => {
        const updated = { ...prev };

        if (updated[subTaskId]) {
          updated[subTaskId] = updated[subTaskId].filter(ct => ct.id !== id);
        }

        return updated;
      });

      setDeleteModalOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error("Delete failed", error);
      alert("Failed to delete child subtask");
    }
  };

  const handleDeleteSubTask = async () => {
    if (!deleteSubTaskId || !deleteSubTaskTaskId) return;

    try {
      await deleteSubTask(deleteSubTaskId);

      // 🔥 Remove from UI instantly
      setProjects(prev =>
        prev.map(p =>
          p.id !== selectedProjectId
            ? p
            : {
              ...p,
              tasks: p.tasks?.map(t =>
                t.id === deleteSubTaskTaskId
                  ? {
                    ...t,
                    subTasks: t.subTasks.filter(st => st.id !== deleteSubTaskId)
                  }
                  : t
              )
            }
        )
      );

      toast.success("Subtask deleted successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete subtask");
    } finally {
      setShowDeleteSubTaskModal(false);
      setDeleteSubTaskId(null);
      setDeleteSubTaskTaskId(null);
    }
  };





  //#region Count Days

  const countWeekdaysSigned = (start: Date, end: Date) => {
    const a = new Date(start);
    const b = new Date(end);
    a.setHours(0, 0, 0, 0);
    b.setHours(0, 0, 0, 0);

    if (a.getTime() === b.getTime()) return 0;

    const forward = b > a;
    let d = new Date(a);
    let count = 0;

    d.setDate(d.getDate() + (forward ? 1 : -1));

    while (forward ? d <= b : d >= b) {
      const day = d.getDay(); // 0=Sun, 6=Sat
      if (day !== 0 && day !== 6) {
        count += forward ? 1 : -1;
      }
      d.setDate(d.getDate() + (forward ? 1 : -1));
    }

    return count;
  };

  const projectStatus = getProjectOverallStatus(allTasksForProject);

  const handleWarningIconClick = () => {
    if (!selectedProject) return;
    const message = getDueDateMessage(selectedProject.endDate, projectStatus);
    if (message) {
      toast.warning(message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
      });
    }
  };

  const entity = selectedEntity;



  const mappedTeamMembers = teamMembers.map(tm => ({
    userId: tm.userID,
    userName: tm.name
  }));


  const preloadAllChildSubtasks = async (tasks: TaskDTO[]) => {
    for (const task of tasks) {
      for (const sub of task.subTasks) {
        if (!childTasks[sub.id]) {
          try {
            const res = await getChildSubTaskById(sub.id);
            const data = Array.isArray(res.data) ? res.data : [res.data];

            setChildTasks(prev => ({
              ...prev,
              [sub.id]: data
            }));
          } catch (err) {
            console.error("Failed to load child subtasks for", sub.id);
          }
        }
      }
    }
  };








  const getInitials = (name: string) => {
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };


  const UserAvatar = ({ name }: { name: string }) => {
    const initials = getInitials(name);

    return (
      <div
        title={name}
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: "#008BFF",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12,
          fontWeight: 600,
          marginRight: 4,
          cursor: "default",
        }}
      >
        {initials}
      </div>
    );
  };


  type WorkCategory = "MODELLING" | "DRAFTING" | "OTHER";

  const getWorkCategory = (desc?: string): WorkCategory => {
    if (!desc) return "OTHER";

    const d = desc.toLowerCase();

    if (d.includes("model")) return "MODELLING";
    if (d.includes("draft")) return "DRAFTING";

    return "OTHER";
  };


  const category = getWorkCategory(newRowData.childSubTaskDescription);

  const hoursLabel =
    category === "MODELLING"
      ? "Modelling Hours"
      : category === "DRAFTING"
        ? "Drafting Hours"
        : "Hours";

  const typeLabel =
    category === "MODELLING"
      ? "Modelling Type"
      : category === "DRAFTING"
        ? "Drafting Type"
        : "Type";

  // const calculateDepartmentTaskHours = (tasks: TaskDTO[]) => {
  //   let mechSpent = 0;
  //   let mechMax = 0;

  //   let elecSpent = 0;
  //   let elecMax = 0;

  //   tasks.forEach(task => {
  //     const dept = task.createdByDepartment?.toLowerCase().trim();

  //     const spent = getTaskTotal(task);   // child + subtask
  //     const max = getmaxTaskTotal(task);

  //     if (dept === "mechanical") {
  //       mechSpent += spent;
  //       mechMax += max;
  //     }

  //     if (dept === "electrical") {
  //       elecSpent += spent;
  //       elecMax += max;
  //     }
  //   });

  //   const totalSpent = mechSpent + elecSpent;
  //   const totalMax = mechMax + elecMax;

  //   return {
  //     mechanical: { spent: mechSpent, max: mechMax },
  //     electrical: { spent: elecSpent, max: elecMax },
  //     total: { spent: totalSpent, max: totalMax }
  //   };
  // };

  // const departmentTaskHours = selectedProject?.tasks
  //   ? calculateDepartmentTaskHours(selectedProject.tasks)
  //   : {
  //     mechanical: { spent: 0, max: 0 },
  //     electrical: { spent: 0, max: 0 },
  //     total: { spent: 0, max: 0 }
  //   };

  const handleSave = async () => {
    const errors: any = {};

    if (!newRowData.hours) errors.hours = true;
    if (!newRowData.product) errors.product = true;

    if (category === "DRAFTING") {
      if (!newRowData.draftingType) errors.draftingType = true;
      if (!newRowData.quantity) errors.quantity = true;
    }

    // if (!newRowData.description) errors.description = true;

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) return;

    if (isSaving) return; // prevent double click

    setIsSaving(true);

    try {
      await saveRow("new");
      setShowNewRow(false);
    } finally {
      setIsSaving(false);
    }
  };


  const getMaxSubtaskDate = (subTasks: any[]) => {
    if (!subTasks || subTasks.length === 0) return null;

    const validDates = subTasks
      .map(s => s.dueDate ? new Date(s.dueDate) : null)
      .filter((d): d is Date => d !== null);

    if (validDates.length === 0) return null;

    const maxDate = new Date(
      Math.max(...validDates.map(d => d.getTime()))
    );

    return maxDate.toISOString();
  };


  const calculateDiff = (start: Date, end: Date) =>
    useWeekdays
      ? countWeekdaysSigned(start, end)
      : Math.round(
        (end.getTime() - start.getTime()) /
        (1000 * 60 * 60 * 24)
      );











  return (
    <Container fluid>


      <Row className=" mb-1 d-flex justify-content-between align-items-center" style={{ backgroundColor: "#dfe2e4ff", height: "60px", borderRadius: "10px" }}>
        <Col sm={12} md={6} lg={2}>
          <Dropdown className='drop-select shadow-sm rounded-3' isOpen={dropdownOpen} toggle={toggle} style={{ width: 'max-content' }} >
            <DropdownToggle caret color="white" className="w-100 text-start fw-semibold border rounded-3 py-2 px-3 d-flex justify-content-between align-items-center">
              {selectedProject ? (
                <div className="d-flex align-items-center gap-3">
                  <span>
                    {selectedProject.projectID} - {selectedProject.projectName}
                  </span>


                </div>
              ) : (
                "Select a project"
              )}

            </DropdownToggle>



            <DropdownMenu className="custom-dropdown-menu shadow rounded-3 p-2">
              {projects.map((p) => (
                <DropdownItem
                  key={p.id}
                  onClick={() =>
                    handleProjectChange({
                      target: { value: String(p.id) }
                    } as React.ChangeEvent<HTMLInputElement>)}
                  className="custom-dropdown-item rounded-2 py-2"
                >
                  <span className="custom-dropdown-item rounded-2 py-2">
                    {p.projectID} - {p.projectName}
                  </span>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </Col>
        {selectedProject && (

          <Col lg={10} md={7} sm={3} className="project-details">
            {/* ===== Top Controls Row ===== */}
            <div className="d-flex align-items-center gap-2 flex-wrap" >

              {/* 🔢 Project Hours
              <Button color="success" outline className="fw-bold px-3" >
                M: {departmentTaskHours.mechanical.spent} / {departmentTaskHours.mechanical.max}
              </Button>

              <Button color="primary" outline className="fw-bold px-3">
                E : {departmentTaskHours.electrical.spent} / {departmentTaskHours.electrical.max}
              </Button>
              <Button color="dark" outline className="fw-bold px-3">
                T : {departmentTaskHours.total.spent} / {departmentTaskHours.total.max}
              </Button> */}

              {/* 🏢 Department Filter */}
              <Input
                type="select"
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="form-select"
                style={{ width: "180px" }}
              >
                <option value="all">All Departments</option>
                <option value="mechanical">Mechanical</option>
                <option value="electrical">Electrical</option>
              </Input>

              {/* 📊 Status Filter */}
              <Button color="light" onClick={() => setStatusModalOpen(true)}>
                Status Filter
              </Button>

              {/* 🧹 Clear */}
              <Button color="light" onClick={() => setSelectedStatuses([])}>
                Clear
              </Button>

              {/* ⚙ Settings */}
              <Button color="light" onClick={() => setSettingsOpen(true)}>
                <SettingsIcon />
              </Button>

              {/* ⚠ Due Date Warning */}
              {(getDueDateColor(selectedProject.endDate, projectStatus) === "warning" ||
                getDueDateColor(selectedProject.endDate, projectStatus) === "danger") && (
                  <WarningAmberIcon
                    fontSize="large"
                    className="blinking-warning"
                    style={{ color: "#ffbf00ff", cursor: "pointer" }}
                    onClick={handleWarningIconClick}
                  />
                )}

              {/* 👤 Show My Tasks */}
              <Button
                color={showMyTasksOnly ? "primary" : "light"}
                onClick={() => setShowMyTasksOnly(prev => !prev)}
              >
                {showMyTasksOnly ? "Show All Tasks" : "Show My Tasks"}
              </Button>
            </div>
            {/* 🏢 User Filter */}
            <Input
              type="select"
              value={selectedUserFilter}
              onChange={(e) => setSelectedUserFilter(e.target.value)}
              className="form-select"
              style={{ width: "220px" }}
            >
              <option value="all">All Users</option>

              {teamMembers
                .filter((user) => !hiddenUsers.includes(user.name))
                .map((user) => (
                  <option key={user.userID} value={user.userID}>
                    {user.name}
                  </option>
                ))}
            </Input>

            <Input
              type="text"
              placeholder="Search Task/Subtask..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: "220px" }}
            />

            {/* ===== Project Schedule ===== */}
            {/* <div className="mt-2 d-flex align-items-center gap-2 fw-bold">
              <span>Project Schedule:</span>
              <span>{formatDate(selectedProject.startDate)}</span>
              <span>-</span>
              <span>{formatDate(selectedProject.endDate)}</span>
            </div> */}
          </Col>

        )}
      </Row>
      <Card className="shadow-sm border-0 rounded-3 mt-4">


        {statusModalOpen && (
          <Card>
            <CardHeader>
              {/* <div className="fw-bold mb-2">Filter by Status</div> */}

              <Row className="row-cols-1 row-cols-md-4 g-2 fw-semibold align-items-center">

                <Col className="d-flex align-items-center gap-2">
                  <Input
                    type="checkbox"
                    checked={
                      selectedStatuses.length ===
                      allStatuses.length
                    }

                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedStatuses(
                          allStatuses.map(
                            (s) => s.value
                          )
                        );
                      } else {
                        setSelectedStatuses([]);
                      }
                    }}
                  />
                  <span>Select All</span>
                </Col>

                {/* <Col className="d-flex align-items-center gap-2">
                  <Input
                    type="checkbox"
                    onChange={() => setSelectedStatuses([])}
                  />
                  <span>Unselect All</span>
                </Col> */}

                <Col className="d-flex align-items-center gap-2">
                  <Input
                    type="checkbox"
                    checked={inProgressStatuses.every(s => selectedStatuses.includes(s))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedStatuses(Array.from(new Set([...selectedStatuses, ...inProgressStatuses])));
                      } else {
                        setSelectedStatuses(selectedStatuses.filter(s => !inProgressStatuses.includes(s)));
                      }
                    }}
                  />
                  <span>Select All In-Progress</span>
                </Col>
                <Col className="d-flex align-items-center gap-2">
                  <Input
                    type="checkbox"
                    checked={ToDoStatuses.every(s => selectedStatuses.includes(s))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedStatuses(Array.from(new Set([...selectedStatuses, ...ToDoStatuses])));
                      } else {
                        setSelectedStatuses(selectedStatuses.filter(s => !ToDoStatuses.includes(s)));
                      }
                    }}
                  />
                  <span>Select All ToDo</span>
                </Col>

                <Col className="d-flex align-items-center gap-2">
                  <Input
                    type="checkbox"
                    checked={completedStatuses.every(s => selectedStatuses.includes(s))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedStatuses(Array.from(new Set([...selectedStatuses, ...completedStatuses])));
                      } else {
                        setSelectedStatuses(selectedStatuses.filter(s => !completedStatuses.includes(s)));
                      }
                    }}
                  />
                  <span>Select All Completed</span>
                  <Button style={{ marginLeft: "200px" }} close onClick={() => setStatusModalOpen(false)} />
                </Col>


              </Row>
            </CardHeader>


            <CardBody style={{ padding: "20px" }}>

              {/* --------- Master Controls --------- */}


              {/* --------- Status List --------- */}
              <Row className="row-cols-6">
                {allStatuses.map((status) => (
                  <Col
                    key={status.value}
                    className="mb-2"
                  >
                    <div className="d-flex align-items-center">

                      <Input
                        id={`status-${status.value}`}
                        type="checkbox"

                        checked={selectedStatuses.includes(
                          status.value
                        )}

                        onChange={() => {

                          selectedStatuses.includes(
                            status.value
                          )

                            ? setSelectedStatuses(
                              selectedStatuses.filter(
                                (s) =>
                                  s !==
                                  status.value
                              )
                            )

                            : setSelectedStatuses([
                              ...selectedStatuses,
                              status.value,
                            ]);
                        }}
                      />

                      <Label
                        check
                        htmlFor={`status-${status.value}`}
                        className="ms-2"
                        style={{
                          cursor: "pointer",
                        }}
                      >
                        {status.label}
                      </Label>

                    </div>
                  </Col>
                ))}
              </Row>


            </CardBody>
          </Card>
        )}



        <CardBody>




          {selectedProjectId === null && <Alert color="info" className="text-center">Please select a project to view its tasks.</Alert>}
          {loadingTasks && <div className="text-center p-4"><Spinner /> <p>Loading tasks...</p></div>}
          {tasksError && <Alert color="danger">{tasksError}</Alert>}
          {!loadingTasks && selectedProject && filteredTasks.length === 0 && (
            <Alert color="info" className="text-center">
              {searchTerm ? `No tasks found for "${searchTerm}".` : "No tasks found for this project."}
            </Alert>
          )}

          {!loadingTasks && selectedProject && (
            <div className="task-table-wrapper">

              <Table responsive size="sm" style={{ tableLayout: 'fixed', minWidth: "800px" }}  >
                <thead style={{ backgroundImage: 'linear-gradient(to right, #004080, #0059b3)', color: 'white' }}>
                  <tr>
                    {effectiveColumns.map((col, index) =>
                      visibleColumns.includes(col.key) && (
                        <th key={col.key} style={{ width: col.width, cursor: 'pointer', userSelect: 'none' }}
                          draggable onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, index)}
                          onClick={() => handleSort(col.key)} >
                          {
                            col.key === "completion" ? (
                              <td
                                color={useWeekdays ? "light" : "light"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // 🔥 prevent sort on button click
                                  setUseWeekdays(prev => {
                                    const v = !prev; localStorage.setItem("useWeekdays", String(v)); return v;
                                  });
                                }}
                                title={useWeekdays ?
                                  "Switch to Calendar Days (Mon – Sun)" :
                                  "Switch to Working Days (Mon – Fri)"} >
                                Due {useWeekdays ? "(WD)" : "(CD)"}
                              </td>) : (col.label)} </th>

                      )
                    )}
                  </tr>

                </thead>

                <tbody>
                  {[...sortedTasks]
                    .filter(task => {
                      if (!showCompletedReleased && (task.status === "" || task.status === "Released")) return false;
                      if (!showNotInScope && task.status === "NotInScope") return false; return true;
                    })
                    .sort((a, b) => {
                      const completedStatuses = ['Completed', 'Released'];

                      if (completedStatuses.includes(a.status) && !completedStatuses.includes(b.status)) return 1;
                      if (completedStatuses.includes(b.status) && !completedStatuses.includes(a.status)) return -1;
                      return 0;
                    })
                    .map((task) => (
                      <React.Fragment key={task.id}>
                        {/* Inline Edit Task Row */}
                        {inlineEditTaskId === task.id ? (
                          <tr>
                            <td colSpan={effectiveColumns.length} className="p-0">
                              <div className="d-flex align-items-center">
                                <div className="flex-grow-1">
                                  <EditTaskComponents
                                    taskId={task.id}
                                    onClose={(refresh = false) => closeInlineEditTask(refresh)}
                                  />
                                </div>
                                <Button
                                  size="sm"
                                  color="secondary"
                                  onClick={() => closeInlineEditTask(false)}
                                  className="m-2"
                                >
                                  Cancel
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          /* ==== Main Task Row ==== */
                          <tr style={{ backgroundColor: '#d8d8dbff' }}>
                            {effectiveColumns.map(col => {
                              if (!visibleColumns.includes(col.key)) return null; // 👈 THIS LINE ADDED

                              switch (col.key) {
                                case "number":
                                  return (
                                    <td key={col.key} className="align-middle text-left">
                                      {/* Add Button */}

                                      {/* {currentUser?.name === task.assignedToName && (
                                        <AddAlarmIcon
                                          onClick={() => {
                                            setSelectedTaskForTime(task);
                                            setSelectedSubTaskForTime(null); // task-only
                                            toggleAddModal();
                                          }}
                                        />)} */}

                                      {/* Expand / Collapse */}
                                      {task.subTasks && (
                                        <Button
                                          color="link"
                                          size="sm"
                                          onClick={() => toggleSubtasksDropdown(task.id)}
                                          style={{ padding: 0 }}
                                        >
                                          {showSubtasksForTask[task.id] ? (
                                            <ExpandLessIcon />
                                          ) : (
                                            <ExpandMoreIcon />
                                          )}
                                        </Button>
                                      )}
                                    </td>
                                  );

                                case "description":
                                  return (
                                    <td key={col.key} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }} data-label="Description">
                                      {task.description}
                                    </td>
                                  );
                                case "spentHours": {
                                  const spent = task.totalSpent ?? 0;

                                  return (
                                    <td>
                                      {toDisplayValue(spent)}{unitLabel}
                                    </td>
                                  );
                                }
                                case "maxHours": {
                                  const max = task.totalMax ?? 0;
                                  return <td>{toDisplayValue(max)}{unitLabel}</td>;
                                }


                                case "Remaining": {
                                  const spent = task.totalSpent ?? 0;
                                  const max = task.totalMax ?? 0;

                                  const remaining = max - spent;

                                  return (
                                    <td style={{ color: remaining < 0 ? "red" : "inherit" }}>
                                      {toDisplayValue(remaining)}{unitLabel}
                                    </td>
                                  );
                                }


                                case "assigned":
                                  return (
                                    <td key={col.key} data-label="Assigned">
                                      {teamMembers.find(m => m.userID === task.assignedToId)?.name || '—'}
                                    </td>
                                  );
                                case "status":
                                  return (
                                    <td key={col.key} >
                                      <Badge className={statusColorMap[task.status] || 'status-pending'}>
                                        {formatStatusLabel(task.status)}
                                      </Badge>
                                    </td>
                                  );

                                case "hours": {

                                  const spent = task.totalSpent ?? 0;
                                  const max = task.totalMax ?? 0;
                                  const remaining = max - spent;

                                  const hoursPercent =
                                    max > 0
                                      ? Math.min(100, Math.round((spent / max) * 100))
                                      : 0;

                                  const isMatch = hoursPercent === task.percentCompleted;

                                  return (
                                    <td key={col.key} style={{ minWidth: "120px" }}>
                                      <Tooltip
                                        arrow
                                        title={
                                          <> <div>Max: {toDisplayValue(max)}{unitLabel}</div>
                                            <div>Spent: {toDisplayValue(spent)}{unitLabel}</div>
                                            <div>Rem: {toDisplayValue(remaining)}{unitLabel}</div>
                                            {/* <div>{hoursPercent}%</div> */}
                                          </>
                                        }
                                      >
                                        <div className="d-flex flex-column gap-1">
                                          <Progress
                                            value={hoursPercent}
                                            max={100}
                                            color={isMatch ? "primary" : "danger"}
                                            style={{ cursor: "pointer" }}
                                          >
                                          </Progress>
                                        </div>
                                      </Tooltip>
                                    </td>
                                  );
                                }

                                case "percent":
                                  return (
                                    <td key={col.key} style={{ minWidth: "120px" }}>
                                      <Tooltip
                                        arrow
                                        title={
                                          <> <div>{task.percentCompleted}% Completed</div>
                                          </>
                                        }
                                      >
                                        <div className="d-flex flex-column gap-1">
                                          <Progress
                                            value={task.percentCompleted}
                                            max={100}
                                            color="primary"
                                          >

                                          </Progress>
                                        </div>
                                      </Tooltip>
                                    </td>
                                  );


                                case "priority":
                                  return (
                                    <td data-label="Priority">
                                      <Badge key={col.key} className={priorityColorMap[task.priority] || 'priority-medium'}>
                                        {task.priority}
                                      </Badge>
                                    </td>
                                  );
                                case "dueDate": {
                                  const maxDate = getMaxSubtaskDate(task.subTasks);

                                  const displayDate = maxDate
                                    ? maxDate
                                    : task.dueDate;

                                  return (
                                    <td key={col.key} data-label="Due Date">
                                      {displayDate ? formatDate(displayDate) : "—"}

                                      <span
                                        className="text-black"
                                        title={task.isDueDateLocked ? "Due date locked" : ""}
                                      >
                                        {task.isDueDateLocked && (
                                          <HttpsIcon fontSize="small" />
                                        )}
                                      </span>
                                    </td>
                                  );
                                }

                                // case "completion":
                                //   return <td key={col.key} data-label="Completion Date">{formatDate(task.completionDate)}</td>;


                                case "completion": {
                                  if (!task.subTasks || task.subTasks.length === 0)
                                    return <td>—</td>;

                                  const maxDateString = getMaxSubtaskDate(task.subTasks);
                                  if (!maxDateString) return <td>—</td>;

                                  const due = new Date(maxDateString);
                                  const today = new Date();
                                  const completed = task.completionDate
                                    ? new Date(task.completionDate)
                                    : null;

                                  const status = task.status?.toLowerCase();

                                  // normalize time
                                  due.setHours(0, 0, 0, 0);
                                  today.setHours(0, 0, 0, 0);
                                  completed?.setHours(0, 0, 0, 0);

                                  const isCompleted =
                                    status === "released" && completed !== null;

                                  let diff = 0;
                                  let tooltipText = "";
                                  let displayText = "";

                                  // ✅ COMPLETED TASK
                                  if (isCompleted) {
                                    diff = calculateDiff(due, completed!);

                                    if (diff === 0) {
                                      tooltipText = "Completed On Time";
                                    } else if (diff < 0) {
                                      tooltipText = `Completed Early by ${Math.abs(diff)} days`;
                                    } else {
                                      tooltipText = `Completed Late by ${diff} days`;
                                    }

                                    displayText = "Completed";
                                  }
                                  else {
                                    // ✅ IMPORTANT: today first, due second
                                    diff = calculateDiff(today, due);

                                    if (diff === 0) {
                                      displayText = "0 day";
                                      tooltipText = "Due today";
                                    } else if (diff < 0) {
                                      displayText = `${diff} day${Math.abs(diff) === 1 ? "" : "s"}`;
                                      tooltipText = `Overdue by ${Math.abs(diff)} days`;
                                    } else {
                                      displayText = `${diff} day${diff === 1 ? "" : "s"}`;
                                      tooltipText = `${diff} days remaining`;
                                    }
                                  }

                                  const color = getDueColor(diff, isCompleted);

                                  return (
                                    <td title={tooltipText} style={{ color, fontWeight: 600 }}>
                                      {displayText}
                                    </td>
                                  );
                                }




                                // ====================== TASK COMMENT CELL ======================
                                case "comment": {
                                  const total = totalCommentCount[`task-${task.id}`] ?? 0;
                                  const unread = unreadCounts[`task-${task.id}`] ?? 0;

                                  return (
                                    <td data-label="Comment">
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="position-relative"
                                        onClick={() => {
                                          openComments("task", task.id, task.description);

                                          setUnreadCounts(prev => {
                                            const updated = { ...prev, [`task-${task.id}`]: 0 };
                                            localStorage.setItem("unreadCounts", JSON.stringify(updated));
                                            return updated;
                                          });
                                        }}

                                      >

                                        {/* 🟦 NO COMMENTS */}
                                        {total === 0 && (
                                          <ChatBubbleOutlineIcon fontSize="small" style={{ color: "blue", opacity: 0.6 }} />
                                        )}

                                        {/* 🟢 ALL READ (if comments exist but no unread) */}
                                        {total > 0 && unread === 0 && (
                                          <ChatIcon fontSize="small" style={{ color: "#0084ffff" }} />
                                        )}

                                        {/* 🔴 UNREAD COMMENTS */}
                                        {unread > 0 && (
                                          <>
                                            <MarkChatUnreadIcon fontSize="small" style={{ color: "blue" }} />

                                            <Badge
                                              color="danger"
                                              className="position-absolute top-0 start-100 translate-middle badge-pulse"
                                              style={{
                                                background: "#d32f2f",
                                                color: "white",
                                                padding: "3px 6px",
                                                fontSize: "11px",
                                                borderRadius: "16px",
                                                border: "1.5px solid white",
                                                fontWeight: 600,
                                                boxShadow: "0px 2px 6px rgba(0,0,0,0.3)",
                                              }}
                                            >
                                              {unread}
                                            </Badge>
                                          </>
                                        )}
                                      </Button>
                                      {!isEditMode && (
                                        <HelpOutlineIcon
                                          style={{
                                            width: "50px",
                                            cursor: "pointer"
                                          }}
                                          className="ms-1"
                                          onClick={() => {
                                            setSelectedEntity({ type: "task", id: task.id });
                                            setInputModalOpen(true);
                                          }}
                                        >
                                          <title>
                                            {task.isClientInputEnabled
                                              ? "Client input disabled"
                                              : "Ask / View client input"}
                                          </title>


                                        </HelpOutlineIcon>





                                      )}

                                    </td>
                                  );
                                }




                                case "log":
                                  return (
                                    <td data-label="Log">
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="p-0"  // 🔹 Removes extra padding, looks like a plain link
                                        onClick={() => {
                                          setSelectedLogEntity({ type: "task", id: task.id, title: task.description });
                                          loadOverallLogs("task", task.id);
                                        }}
                                      >
                                        {formatDate(task.completionDate)}
                                      </Button>
                                    </td>
                                  );


                                //                                 case "log":
                                // return (
                                //   <td data-label="Log" className="d-flex align-items-center gap-1">
                                //     <span>{formatDate(task.completionDate)}</span>

                                //     <Button
                                //       color="link"
                                //       size="sm"
                                //       onClick={() => {
                                //         setSelectedLogEntity({ type: "task", id: task.id, title: task.description });
                                //         loadOverallLogs("task", task.id);
                                //       }}
                                //     >
                                //       <ManageHistoryIcon />
                                //     </Button>
                                //   </td>
                                // );




                                case "actions":
                                  return (
                                    <td key={col.key} className="text-center">
                                      {(() => {
                                        const assignee = teamMembers.find(m => m.userID === task.assignedToId);
                                        const canEdit = assignee && assignee.department === department;

                                        return canEdit ? (
                                          <div className="d-flex justify-content-center gap-2">

                                            {/* ✏ Edit Button */}
                                            <Button
                                              size="sm"
                                              onClick={() => openInlineEditTask(task.id)}
                                              className="border-0 rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                                              style={{
                                                width: "32px",
                                                height: "32px",
                                                backgroundColor: "#e7f3ff",
                                                transition: "all 0.2s ease-in-out",
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = "#d0e7ff";
                                                e.currentTarget.style.transform = "scale(1.05)";
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = "#e7f3ff";
                                                e.currentTarget.style.transform = "scale(1)";
                                              }}
                                            >
                                              <EditIcon fontSize="small" className="text-primary" />
                                            </Button>

                                            {/* 🗑 Delete Button */}
                                            <Button
                                              size="sm"
                                              onClick={() => openDeleteModal(task.id)}
                                              className="border-0 rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                                              style={{
                                                width: "32px",
                                                height: "32px",
                                                backgroundColor: "#ffe7e7",
                                                transition: "all 0.2s ease-in-out",
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = "#ffcccc";
                                                e.currentTarget.style.transform = "scale(1.05)";
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = "#ffe7e7";
                                                e.currentTarget.style.transform = "scale(1)";
                                              }}
                                            >
                                              <DeleteIcon fontSize="small" className="text-danger" />
                                            </Button>



                                          </div>
                                        ) : null;
                                      })()}
                                    </td>

                                  );
                                default:
                                  return null;
                              }
                            })}
                          </tr>
                        )}

                        {/* ==== Subtasks ==== */}
                        {showSubtasksForTask[task.id] &&
                          [...(task.subTasks ?? [])]
                            .filter(subtask => {
                              if (!showCompletedReleased && (subtask.status === "" || subtask.status === "Released")) return false;
                              if (!showNotInScope && subtask.status === "NotInScope") return false; return true;
                            })
                            .sort((a, b) => {
                              const completedStatuses = ['Completed', 'Released'];
                              if (completedStatuses.includes(a.status) && !completedStatuses.includes(b.status)) return 1;
                              if (completedStatuses.includes(b.status) && !completedStatuses.includes(a.status)) return -1;
                              return 0;
                            })
                            .map((subtask) => (
                              <React.Fragment key={subtask.id}>
                                {inlineEditSubTaskId === subtask.id ? (
                                  <tr>
                                    <td colSpan={effectiveColumns.length} className="p-0">
                                      <EditSubTaskComponent
                                        subTaskId={subtask.id}
                                        onClose={(refresh = false) => closeInlineEditSubTask(refresh, task.id)}
                                      />
                                    </td>
                                  </tr>
                                ) : (
                                  <tr style={{ backgroundColor: '#fffbe6', borderTop: '1px solid #dee2e6' }}>
                                    {effectiveColumns.map((col) => {
                                      if (!visibleColumns.includes(col.key)) return null; // 👈 THIS LINE ADDED
                                      switch (col.key) {
                                        case "number":
                                          return (
                                            <td className="align-middle text-center">
                                              <div className="d-flex justify-content-center align-items-center gap-1">

                                                {/* {currentUser?.name === subtask.assignedToName && (
                                                  <AddAlarmIcon
                                                    onClick={() => {
                                                      setSelectedTaskForTime(task);
                                                      setSelectedSubTaskForTime(subtask);
                                                      toggleAddModal();
                                                    }}
                                                    style={{ cursor: "pointer" }}
                                                  />

                                                )}

                                                <Tooltip title="Add Child SubTask">
                                                  <AddIcon
                                                    sx={{ cursor: "pointer", color: "primary.main" }}
                                                    onClick={() => openInlineCreateChildSubTask(subtask.id)}
                                                  />
                                                </Tooltip> */}

                                                <Button
                                                  size="sm"
                                                  color="link"
                                                  onClick={() =>
                                                    expandedSubTasks[subtask.id]
                                                      ? collapseSubTask(subtask.id)
                                                      : expandSubTask(subtask.id)
                                                  }
                                                >
                                                  {expandedSubTasks[subtask.id]
                                                    ? <ExpandLessIcon />
                                                    : <ExpandMoreIcon />}
                                                </Button>


                                              </div>
                                            </td>
                                          );

                                        case "description":
                                          return (
                                            <td data-label="description"
                                              key={col.key}
                                              style={{
                                                paddingLeft: '2rem',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                              }}
                                            >
                                              {subtask.description}
                                            </td>
                                          );
                                        case "spentHours": {
                                          const spent = subtask.totalSpent ?? 0;

                                          return (
                                            <td>
                                              {/* <Button
        color="link"
        className="p-0"
        onClick={() =>
          loadSpentHoursLogs("subtask", subtask.id, subtask.description)
        }
      > */}
                                              {toDisplayValue(spent)}{unitLabel}
                                            </td>
                                          );
                                        }



                                        case "maxHours": {
                                          const max = subtask.totalMax ?? 0;
                                          return <td>{toDisplayValue(max)}{unitLabel}</td>;
                                        }


                                        case "Remaining": {
                                          const spent = subtask.totalSpent ?? 0;
                                          const max = subtask.totalMax ?? 0;

                                          const remaining = max - spent;

                                          return (
                                            <td style={{ color: remaining < 0 ? "red" : "inherit" }}>
                                              {toDisplayValue(remaining)}{unitLabel}
                                            </td>
                                          );
                                        }


                                        case "assigned":
                                          return (
                                            <td key={col.key} data-label="assigned">
                                              {teamMembers.find(m => m.userID === subtask.assignedToId)?.name || '—'}
                                            </td>
                                          );
                                        case "status":
                                          return (
                                            <td key={col.key}>
                                              <Badge className={statusColorMap[subtask.status] || 'status-pending'}>
                                                {formatStatusLabel(subtask.status)}
                                              </Badge>
                                            </td>
                                          );
                                        case "priority":
                                          return (
                                            <td key={col.key}>
                                              <Badge className={priorityColorMap[subtask.priority] || 'priority-medium'}>
                                                {subtask.priority}
                                              </Badge>
                                            </td>
                                          );

                                        case "dueDate":
                                          return (
                                            <td key={col.key} data-label="Due Date">
                                              {formatDate(subtask.dueDate)}

                                              <span
                                                className='text-black'
                                                title="Due date locked"
                                              >
                                                {subtask.isDueDateLocked && (
                                                  <HttpsIcon fontSize="small" />
                                                )}
                                              </span>
                                            </td>
                                          );


                                        case "completion": {
                                          if (!subtask.dueDate) return <td>—</td>;

                                          const due = new Date(subtask.dueDate);
                                          const today = new Date();
                                          const completed = subtask.completionDate
                                            ? new Date(subtask.completionDate)
                                            : null;

                                          const status = subtask.status?.toLowerCase();

                                          // normalize time
                                          due.setHours(0, 0, 0, 0);
                                          today.setHours(0, 0, 0, 0);
                                          completed?.setHours(0, 0, 0, 0);

                                          const isCompleted =
                                            status === "released" && completed !== null;

                                          const calculateDiff = (start: Date, end: Date) =>
                                            useWeekdays
                                              ? countWeekdaysSigned(start, end)
                                              : Math.round(
                                                (end.getTime() - start.getTime()) /
                                                (1000 * 60 * 60 * 24)
                                              );

                                          let diff = 0;
                                          let tooltipText = "";
                                          let displayText = "";

                                          // ✅ COMPLETED SUBTASK
                                          if (isCompleted) {
                                            diff = calculateDiff(due, completed!);

                                            if (diff === 0) {
                                              tooltipText = "Completed On Time";
                                            } else if (diff < 0) {
                                              tooltipText = `Completed Early by ${Math.abs(diff)} days`;
                                            } else {
                                              tooltipText = `Completed Late by ${diff} days`;
                                            }

                                            displayText = "Completed";
                                          }
                                          // ✅ ACTIVE SUBTASK
                                          else {
                                            // ✅ IMPORTANT: today first, due second
                                            diff = calculateDiff(today, due);

                                            if (diff === 0) {
                                              displayText = "0 day";
                                              tooltipText = "Due today";
                                            } else if (diff < 0) {
                                              displayText = `${diff} day${Math.abs(diff) === 1 ? "" : "s"}`;
                                              tooltipText = `Overdue by ${Math.abs(diff)} days`;
                                            } else {
                                              displayText = `${diff} day${diff === 1 ? "" : "s"}`;
                                              tooltipText = `${diff} days remaining`;
                                            }
                                          }

                                          const color = getDueColor(diff, isCompleted);

                                          return (
                                            <td title={tooltipText} style={{ color, fontWeight: 600 }}>
                                              {displayText}
                                            </td>
                                          );
                                        }


                                        case "hours": {

                                          const spent = subtask.totalSpent ?? 0;
                                          const max = subtask.totalMax ?? 0;
                                          const remaining = max - spent;

                                          const hoursPercent =
                                            max > 0
                                              ? Math.min(100, Math.round((spent / max) * 100))
                                              : 0;

                                          const isMatch = hoursPercent === subtask.percentCompleted;

                                          return (
                                            <td key={col.key} style={{ minWidth: "120px" }}>
                                              <Tooltip
                                                title={
                                                  <>
                                                    <div>Max: {toDisplayValue(max)}{unitLabel}</div>
                                                    <div>Spent: {toDisplayValue(spent)}{unitLabel}</div>
                                                    <div>Rem: {toDisplayValue(remaining)}{unitLabel}</div>
                                                    {/* <div>{hoursPercent}%</div> */}
                                                  </>
                                                }
                                                arrow
                                              >
                                                <div className="d-flex flex-column gap-1">
                                                  <Progress
                                                    value={hoursPercent}
                                                    max={100}
                                                    color={isMatch ? "primary" : "danger"}
                                                    style={{ cursor: "pointer" }}
                                                  >

                                                  </Progress>
                                                </div>
                                              </Tooltip>
                                            </td>
                                          );
                                        }
                                        case "percent":
                                          return (
                                            <td key={col.key} style={{ minWidth: "120px" }}>
                                              <Tooltip
                                                title={
                                                  <>
                                                    <div>{subtask.percentCompleted}% completed</div>
                                                  </>
                                                }
                                                arrow
                                              >

                                                <div className="d-flex flex-column gap-1">
                                                  <Progress
                                                    value={subtask.percentCompleted}
                                                    max={100}
                                                    color="primary"
                                                  >
                                                  </Progress>
                                                </div>
                                              </Tooltip>
                                            </td>
                                          );


                                        case "comment": {
                                          const total = totalCommentCount[`subtask-${subtask.id}`] ?? 0;
                                          const unread = unreadCounts[`subtask-${subtask.id}`] ?? 0;

                                          return (
                                            <td data-label="Comment">
                                              <Button
                                                color="link"
                                                size="sm"
                                                className="position-relative"
                                                onClick={() => {
                                                  openComments("subtask", subtask.id, subtask.description);

                                                  setUnreadCounts(prev => {
                                                    const updated = { ...prev, [`subtask-${subtask.id}`]: 0 };
                                                    localStorage.setItem("unreadCounts", JSON.stringify(updated));
                                                    return updated;
                                                  });
                                                }}

                                              >

                                                {/* 🟦 NO COMMENTS */}
                                                {total === 0 && (
                                                  <ChatBubbleOutlineIcon fontSize="small" style={{ color: "blue", opacity: 0.6 }} />
                                                )}

                                                {/* 🟢 ALL READ */}
                                                {total > 0 && unread === 0 && (
                                                  <ChatIcon fontSize="small" style={{ color: "#0084ffff" }} />
                                                )}

                                                {/* 🔴 UNREAD COMMENTS */}
                                                {unread > 0 && (
                                                  <>
                                                    <MarkChatUnreadIcon fontSize="small" style={{ color: "blue" }} />

                                                    <Badge
                                                      color="danger"
                                                      className="position-absolute top-0 start-100 translate-middle badge-pulse"
                                                      style={{
                                                        background: "#d32f2f",
                                                        color: "white",
                                                        padding: "3px 6px",
                                                        fontSize: "11px",
                                                        borderRadius: "16px",
                                                        border: "1.5px solid white",
                                                        fontWeight: 600,
                                                        boxShadow: "0px 2px 6px rgba(0,0,0,0.3)",
                                                      }}
                                                    >
                                                      {unread}
                                                    </Badge>
                                                  </>
                                                )}
                                              </Button>
                                              {!isEditMode && (
                                                <HelpOutlineIcon
                                                  style={{
                                                    cursor: "pointer"
                                                  }}
                                                  className="ms-1"
                                                  onClick={() => {
                                                    setSelectedEntity({ type: "subtask", id: subtask.id });
                                                    setInputModalOpen(true);
                                                  }}
                                                >
                                                  <title>
                                                    {subtask.isClientInputEnabled
                                                      ? "Client input disabled"
                                                      : "Ask / View client input"}
                                                  </title>

                                                </HelpOutlineIcon>

                                              )}

                                            </td>
                                          );
                                        }

                                        case "log":
                                          return (
                                            <td data-label="Log">
                                              <Button
                                                color="link"
                                                size="sm"
                                                className="p-0"  // 🔹 Removes extra padding, looks like a plain link
                                                onClick={() => {
                                                  setSelectedLogEntity({ type: "subtask", id: subtask.id, title: subtask.description });
                                                  loadOverallLogs("subtask", subtask.id);
                                                }}
                                              >
                                                {formatDate(subtask.completionDate)}
                                              </Button>
                                            </td>
                                          );



                                        case "actions":
                                          return (
                                            <td key={col.key} className="text-center">
                                              {(() => {
                                                const assignee = teamMembers.find(m => m.userID === subtask.assignedToId);
                                                const canEdit = assignee && assignee.department === department;

                                                if (!canEdit) return null;

                                                return (
                                                  <div className="d-flex justify-content-center gap-2">

                                                    {/* ✏ Edit */}
                                                    <Button
                                                      size="sm"
                                                      onClick={() => openInlineEditSubTask(subtask.id, task.id)}
                                                      className="border-0 rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                                                      style={{
                                                        width: "32px",
                                                        height: "32px",
                                                        backgroundColor: "#e7f3ff",
                                                      }}
                                                    >
                                                      <EditIcon fontSize="small" className="text-primary" />
                                                    </Button>

                                                    {/* 🗑 Delete */}
                                                    <Button
                                                      size="sm"
                                                      onClick={() => {
                                                        setDeleteSubTaskId(subtask.id);
                                                        setDeleteSubTaskTaskId(task.id);
                                                        setShowDeleteSubTaskModal(true);
                                                      }}
                                                      className="border-0 rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                                                      style={{
                                                        width: "32px",
                                                        height: "32px",
                                                        backgroundColor: "#ffe7e7",
                                                      }}
                                                    >
                                                      <DeleteIcon fontSize="small" className="text-danger" />
                                                    </Button>

                                                  </div>
                                                );
                                              })()}
                                            </td>
                                          );

                                        default:
                                          return null;

                                      }
                                    })}
                                  </tr>
                                )}


                                {expandedSubTasks[subtask.id] && (
                                  <>
                                    {/* LOADING */}
                                    {inlineNewChildSubTaskId[subtask.id] && (
                                      <tr style={{ backgroundColor: "#fdf4ff" }}>
                                        <td colSpan={effectiveColumns.length}>
                                          <AddChildSubTaskInline
                                            subTaskId={subtask.id}
                                            parentSubTaskDueDate={subtask.dueDate}
                                            onCancel={() =>
                                              closeInlineCreateChildSubTask(subtask.id, false)
                                            }
                                            onCreated={async () => {
                                              closeInlineCreateChildSubTask(subtask.id, true);
                                            }}
                                          />
                                        </td>
                                      </tr>
                                    )}

                                    {loadingChildTasks.includes(subtask.id) ? (
                                      <tr>
                                        <td colSpan={effectiveColumns.length} className="text-center">
                                          <Spinner size="sm" /> Loading child tasks...

                                        </td>
                                      </tr>
                                    ) : (
                                      (childTasks[subtask.id] || []).map((ct: any) =>
                                        editingChildId === ct.id ? (
                                          <tr key={ct.id}>
                                            <td colSpan={effectiveColumns.length}>
                                              <EditChildSubTaskInline
                                                childTask={ct}
                                                onClose={(refresh) => {
                                                  setEditingChildId(null);
                                                  if (refresh) {
                                                    setChildTasks(prev => {
                                                      const copy = { ...prev };
                                                      delete copy[subtask.id];
                                                      return copy;
                                                    });
                                                    expandSubTask(subtask.id);
                                                  }
                                                }}

                                              />
                                            </td>
                                          </tr>
                                        )
                                          : (
                                            <tr key={ct.id} style={{ backgroundColor: "#f9fafb" }}>


                                              {effectiveColumns.map(col => {
                                                if (!visibleColumns.includes(col.key)) return null;

                                                switch (col.key) {

                                                  case "number":
                                                    return (
                                                      <td key={col.key}>
                                                        {ct.teamMemberNames?.includes(currentUser?.name || "") && (
                                                          <AddAlarmIcon
                                                            onClick={() => {
                                                              setSelectedTaskForTime(task);
                                                              setSelectedSubTaskForTime(subtask);
                                                              setSelectedChildSubTaskForTime(ct);
                                                              toggleAddModal();
                                                            }}
                                                            style={{ cursor: "pointer" }}
                                                          />
                                                        )}
                                                      </td>
                                                    );






                                                  case "description":
                                                    return (
                                                      <td className="ps-5">
                                                        ↳ {ct.description}
                                                      </td>
                                                    );

                                                  case "spentHours": {
                                                    const spent = ct.spentHours ?? 0;

                                                    return (
                                                      <td>
                                                        <Button
                                                          color="link"
                                                          className="p-0 "
                                                          onClick={() =>
                                                            loadSpentHoursLogs(
                                                              "childSubTask",
                                                              ct.id,
                                                              ct.description
                                                            )
                                                          }
                                                        >
                                                          {toDisplayValue(spent)}{unitLabel}
                                                        </Button>
                                                      </td>
                                                    );
                                                  }

                                                  case "maxHours":
                                                    return (
                                                      <td>
                                                        {toDisplayValue(ct.maxHours ?? 0)}{unitLabel}
                                                      </td>
                                                    );

                                                  case "Remaining": {
                                                    const remaining =
                                                      (ct.maxHours ?? 0) - (ct.spentHours ?? 0);
                                                    return (
                                                      <td style={{ color: remaining < 0 ? "red" : "inherit" }}>
                                                        {toDisplayValue(remaining)}{unitLabel}
                                                      </td>
                                                    );
                                                  }

                                                  case "hours": {
                                                    const spent = ct.spentHours ?? 0;
                                                    const max = ct.maxHours ?? 0;
                                                    const remaining = max - spent;

                                                    const percent = calculatePercent(spent, max);

                                                    return (
                                                      <td style={{ minWidth: "100px" }}>
                                                        <Tooltip
                                                          arrow
                                                          title={
                                                            <>
                                                              <div>Max: {toDisplayValue(max)}{unitLabel}</div>
                                                              <div>Spent: {toDisplayValue(spent)}{unitLabel}</div>
                                                              <div>Rem: {toDisplayValue(remaining)}{unitLabel}</div>
                                                              {/* <div>{percent}%</div> */}
                                                            </>
                                                          }
                                                        >
                                                          <div>
                                                            <Progress
                                                              value={percent}
                                                              style={{ cursor: "pointer" }}
                                                            >

                                                            </Progress>
                                                          </div>
                                                        </Tooltip>
                                                      </td>
                                                    );
                                                  }
                                                  case "percent": {
                                                    return (
                                                      <td style={{ minWidth: "100px" }}>
                                                        <Tooltip
                                                          arrow
                                                          title={
                                                            <>
                                                              <div>{ct.percentCompleted}% Completed</div>
                                                            </>
                                                          }
                                                        >
                                                          <Progress
                                                            value={ct.percentCompleted}
                                                          >

                                                          </Progress>
                                                        </Tooltip>
                                                      </td>
                                                    );
                                                  }

                                                  case "assigned":
                                                    return (
                                                      <td key={col.key}>
                                                        {ct.teamMemberNames && ct.teamMemberNames.length > 0 ? (
                                                          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                                                            {ct.teamMemberNames.map((name: string, idx: number) => (
                                                              <UserAvatar key={idx} name={name} />
                                                            ))}
                                                          </div>
                                                        ) : (
                                                          <span className="text-muted">—</span>
                                                        )}
                                                      </td>
                                                    );



                                                  case "status":
                                                    return (
                                                      <td>
                                                        <Badge className={statusColorMap[ct.status] || "secondary"}>
                                                          {childformatStatusLabel(ct.status)}
                                                        </Badge>
                                                      </td>
                                                    );

                                                  case "priority":
                                                    return (
                                                      <td>
                                                        <Badge className={priorityColorMap[ct.priority] || "secondary"}>
                                                          {ct.priority}
                                                        </Badge>
                                                      </td>
                                                    );

                                                  case "dueDate":
                                                    return <td>{formatDate(ct.dueDate)}</td>;

                                                  case "completion": {
                                                    if (!ct.dueDate) return <td>—</td>;

                                                    const due = new Date(ct.dueDate);
                                                    const today = new Date();
                                                    const completed = ct.completionDate
                                                      ? new Date(ct.completionDate)
                                                      : null;

                                                    const status = ct.status?.toLowerCase();

                                                    // normalize time
                                                    due.setHours(0, 0, 0, 0);
                                                    today.setHours(0, 0, 0, 0);
                                                    completed?.setHours(0, 0, 0, 0);

                                                    const isCompleted =
                                                      status === "released" && completed !== null;

                                                    const calculateDiff = (start: Date, end: Date) =>
                                                      useWeekdays
                                                        ? countWeekdaysSigned(start, end)
                                                        : Math.round(
                                                          (end.getTime() - start.getTime()) /
                                                          (1000 * 60 * 60 * 24)
                                                        );

                                                    let diff = 0;
                                                    let tooltipText = "";
                                                    let displayText = "";

                                                    // ✅ COMPLETED CHILD TASK
                                                    if (isCompleted) {
                                                      diff = calculateDiff(due, completed!);

                                                      if (diff === 0) {
                                                        tooltipText = "Completed On Time";
                                                      } else if (diff < 0) {
                                                        tooltipText = `Completed Early by ${Math.abs(diff)} days`;
                                                      } else {
                                                        tooltipText = `Completed Late by ${diff} days`;
                                                      }

                                                      displayText = "Completed";
                                                    }
                                                    // ✅ ACTIVE CHILD TASK
                                                    else {
                                                      // ✅ IMPORTANT: today first, due second
                                                      diff = calculateDiff(today, due);

                                                      if (diff === 0) {
                                                        displayText = "0 day";
                                                        tooltipText = "Due today";
                                                      } else if (diff < 0) {
                                                        displayText = `${diff} day${Math.abs(diff) === 1 ? "" : "s"}`;
                                                        tooltipText = `Overdue by ${Math.abs(diff)} days`;
                                                      } else {
                                                        displayText = `${diff} day${diff === 1 ? "" : "s"}`;
                                                        tooltipText = `${diff} days remaining`;
                                                      }
                                                    }

                                                    const color = getDueColor(diff, isCompleted);

                                                    return (
                                                      <td title={tooltipText} style={{ color, fontWeight: 600 }}>
                                                        {displayText}
                                                      </td>
                                                    );
                                                  }

                                                  case "comment": {
                                                    const total = totalCommentCount[`childSubTask-${ct.id}`] ?? 0;
                                                    const unread = unreadCounts[`childSubTask-${ct.id}`] ?? 0;

                                                    return (
                                                      <td data-label="Comment">
                                                        <Button
                                                          color="link"
                                                          size="sm"
                                                          className="position-relative"
                                                          onClick={() => {
                                                            openComments("childSubTask", ct.id, ct.description);

                                                            setUnreadCounts(prev => {
                                                              const updated = { ...prev, [`childSubTask-${ct.id}`]: 0 };
                                                              localStorage.setItem("unreadCounts", JSON.stringify(updated));
                                                              return updated;
                                                            });
                                                          }}

                                                        >

                                                          {/* 🟦 NO COMMENTS */}
                                                          {total === 0 && (
                                                            <ChatBubbleOutlineIcon fontSize="small" style={{ color: "blue", opacity: 0.6 }} />
                                                          )}

                                                          {/* 🟢 ALL READ */}
                                                          {total > 0 && unread === 0 && (
                                                            <ChatIcon fontSize="small" style={{ color: "#0084ffff" }} />
                                                          )}

                                                          {/* 🔴 UNREAD COMMENTS */}
                                                          {unread > 0 && (
                                                            <>
                                                              <MarkChatUnreadIcon fontSize="small" style={{ color: "blue" }} />

                                                              <Badge
                                                                color="danger"
                                                                className="position-absolute top-0 start-100 translate-middle badge-pulse"
                                                                style={{
                                                                  background: "#d32f2f",
                                                                  color: "white",
                                                                  padding: "3px 6px",
                                                                  fontSize: "11px",
                                                                  borderRadius: "16px",
                                                                  border: "1.5px solid white",
                                                                  fontWeight: 600,
                                                                  boxShadow: "0px 2px 6px rgba(0,0,0,0.3)",
                                                                }}
                                                              >
                                                                {unread}
                                                              </Badge>
                                                            </>
                                                          )}
                                                        </Button>
                                                        {/* {!isEditMode && (
                                                <HelpOutlineIcon
                                                  style={{
                                                    cursor: "pointer"
                                                  }}
                                                  className="ms-1"
                                                  onClick={() => {
                                                    setSelectedEntity({ type: "childSubTask", id: ct.id });
                                                    setInputModalOpen(true);
                                                  }}
                                                >
                                                  <title>
                                                    {ct.isClientInputEnabled
                                                      ? "Client input disabled"
                                                      : "Ask / View client input"}
                                                  </title>

                                                </HelpOutlineIcon>

                                              )} */}

                                                      </td>
                                                    );
                                                  }

                                                  case "log":
                                                    return (
                                                      <td data-label="Log">
                                                        <Button
                                                          color="link"
                                                          size="sm"
                                                          className="p-0"  // 🔹 Removes extra padding, looks like a plain link
                                                          onClick={() => {
                                                            setSelectedLogEntity({ type: "childSubTask", id: ct.id, title: ct.description });
                                                            loadOverallLogs("childSubTask", ct.id);
                                                          }}
                                                        >
                                                          {formatDate(ct.completionDate)}
                                                        </Button>
                                                      </td>
                                                    );


                                                  case "actions":
                                                    return (
                                                      <td key={col.key} className="text-center">
                                                        {(() => {
                                                          const assignedMembers = teamMembers.filter(m =>
                                                            ct.teamMemberNames?.includes(m.name)
                                                          );

                                                          const canEdit = assignedMembers.some(
                                                            m => m.department.toLowerCase() === department?.toLowerCase()
                                                          );

                                                          if (!canEdit) return null;

                                                          return (
                                                            <div className="d-flex justify-content-center gap-2">

                                                              {/* EDIT */}
                                                              <Button
                                                                size="sm"
                                                                onClick={() => setEditingChildId(ct.id)}
                                                                className="border-0 rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                                                                style={{
                                                                  width: "32px",
                                                                  height: "32px",
                                                                  backgroundColor: "#e7f3ff",
                                                                }}
                                                              >
                                                                <EditIcon fontSize="small" className="text-primary" />
                                                              </Button>

                                                              {/* DELETE */}
                                                              <Button
                                                                size="sm"
                                                                onClick={() => {
                                                                  setDeleteTarget({ id: ct.id, subTaskId: ct.subTaskId });
                                                                  setDeleteModalOpen(true);
                                                                }}


                                                                className="border-0 rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                                                                style={{
                                                                  width: "32px",
                                                                  height: "32px",
                                                                  backgroundColor: "#ffe7e7",
                                                                }}
                                                              >
                                                                <DeleteIcon fontSize="small" className="text-danger" />
                                                              </Button>

                                                            </div>
                                                          );
                                                        })()}
                                                      </td>
                                                    );



                                                  default:
                                                    return <td />;
                                                }
                                              })}
                                            </tr>

                                          )
                                      )
                                    )}
                                    <tr>
                                      <td
                                        colSpan={visibleColumns.length}
                                        style={{
                                          background: "#f5f7fb",
                                          padding: "0px",
                                          borderTop: "1px solid #d6dbe3"
                                        }}
                                      >
                                        <div
                                          style={{
                                            height: "470px",
                                            overflowY: "auto",
                                            overflowX: "hidden",
                                            padding: "15px",
                                            background: "#ffffff",
                                            borderRadius: "6px",
                                          }}
                                        >
                                          <Comments
                                            isOpen={false}
                                            onClose={() => { }}
                                            entityType="subtask"
                                            entityId={subtask.id}
                                            title={subtask.description}
                                            inlineMode={true}
                                          />
                                        </div>
                                      </td>
                                    </tr>

                                  </>
                                )}
                              </React.Fragment>
                            ))}



                        {/* ==== Add New Subtask Button ==== */}
                        {showSubtasksForTask[task.id] && (
                          <tr style={{ backgroundColor: '#fffbe6' }}>


                            <td className="align-middle" style={{ paddingLeft: '2rem' }}>
                              {canCreate && (
                                <Button
                                  size="sm"
                                  outline
                                  color={inlineNewSubtaskTaskId[task.id] ? "secondary" : "warning"}
                                  onClick={() => {
                                    if (inlineNewSubtaskTaskId[task.id]) {
                                      closeInlineCreateSubTask(task.id, false);
                                    } else {
                                      openInlineCreateSubTask(task.id);
                                    }
                                  }}
                                  className="d-flex align-items-center justify-content-center p-2 rounded-circle"
                                  style={{ width: '32px', height: '32px', marginRight: '5px' }}
                                  title={
                                    inlineNewSubtaskTaskId[task.id]
                                      ? "Close Add Subtask Form"
                                      : "Add New Subtask"
                                  }
                                >
                                  {inlineNewSubtaskTaskId[task.id] ? (
                                    <ExpandLessIcon fontSize="small" />
                                  ) : (
                                    <AddIcon fontSize="small" />
                                  )}
                                </Button>
                              )}
                            </td>


                            <td colSpan={effectiveColumns.length - 1} className="p-0 align-middle">
                              {canCreate &&
                                inlineNewSubtaskTaskId[task.id] &&
                                !inlineEditSubTaskId &&
                                !inlineEditTaskId && (
                                  <AddSubTaskInline
                                    taskId={task.id}
                                    projectId={selectedProject?.projectID || ''}
                                    teamMembers={teamMembers}
                                    parentTaskDueDate={task.dueDate}
                                    onCancel={() => closeInlineCreateSubTask(task.id, false)}
                                    onSubTaskAdded={() => closeInlineCreateSubTask(task.id, true)}
                                  />
                                )}

                            </td>


                          </tr>
                        )}
                      </React.Fragment>
                    ))}

                  {/* ==== Add New Task Button ==== */}
                  {inlineNewTaskProjectId === selectedProjectId &&
                    !inlineEditTaskId &&
                    !inlineEditSubTaskId &&
                    !Object.values(inlineNewSubtaskTaskId).some(Boolean) && (
                      <tr>
                        <td style={{ padding: '8px' }}>
                          {canCreate && (
                            <Button
                              size="sm"
                              outline
                              color={showInlineCreateTask ? "danger" : "success"}
                              onClick={showInlineCreateTask ? closeInlineCreateTask : openInlineCreateTask}
                              className="d-flex align-items-center justify-content-center p-2 rounded-circle"
                              style={{ width: '32px', height: '32px' }}
                              title={showInlineCreateTask ? "Close Add Task Form" : "Add New Task"}
                            >
                              {showInlineCreateTask ? (
                                <ExpandLessIcon fontSize="small" />
                              ) : (
                                <AddIcon fontSize="small" />
                              )}
                            </Button>
                          )}
                        </td>
                        <td colSpan={effectiveColumns.length - 1} className="p-0 align-middle">
                          {canCreate &&
                            showInlineCreateTask &&
                            selectedProject && (
                              <AddTaskSubTaskComponents
                                isOpen={true}
                                toggle={closeInlineCreateTask}
                                onTaskCreated={() => {
                                  closeInlineCreateTask();
                                  refreshTasksList();
                                }}
                                initialProjectId={selectedProjectId?.toString()}
                                projectEndDate={selectedProject.endDate}
                              />
                            )}

                        </td>
                      </tr>
                    )}
                </tbody>
              </Table>
            </div>
          )}



          {commentPopup?.open && (
            <Comments
              isOpen={commentPopup.open}
              onClose={() => {
                // Refresh unread count only for task/subtask
                if (commentPopup.type === "task" || commentPopup.type === "subtask") {
                  loadUnreadCountFor(commentPopup.type, commentPopup.id);
                }
                setCommentPopup(null);
              }}

              entityType={commentPopup.type}
              entityId={commentPopup.id}
              title={commentPopup.title}
            />
          )}

          {/* ================== SETTINGS MODAL ================== */}
          <Modal isOpen={settingsOpen} toggle={() => setSettingsOpen(false)} centered>
            <ModalHeader toggle={() => setSettingsOpen(false)}>Settings</ModalHeader>
            <ModalBody>

              {/* Toggle Percent Column */}
              <div className="d-flex mb-2">
                <Label>
                  <Input
                    type="checkbox"
                    checked={visibleColumns.includes("percent")}
                    onChange={() => {
                      setVisibleColumns(prev => {
                        const updated = prev.includes("percent")
                          ? prev.filter(c => c !== "percent")
                          : [...prev, "percent"];

                        localStorage.setItem("visibleColumns", JSON.stringify(updated)); // 🔥 Save permanently
                        return updated;
                      });
                    }}
                  />
                  <span className="ms-2">Show Work Done</span>
                </Label>
              </div>
              <div className="d-flex mb-2">
                <Label>
                  <Input
                    type="checkbox"
                    checked={
                      visibleColumns.includes("spentHours") &&
                      visibleColumns.includes("maxHours") &&
                      visibleColumns.includes("Remaining")
                    }
                    onChange={() => {
                      setVisibleColumns(prev => {
                        const hasAll =
                          prev.includes("spentHours") &&
                          prev.includes("maxHours") &&
                          prev.includes("Remaining");

                        const updated = hasAll
                          ? prev.filter(
                            c => !["spentHours", "maxHours", "Remaining"].includes(c)
                          )
                          : [
                            ...prev,
                            ...["spentHours", "maxHours", "Remaining"].filter(
                              c => !prev.includes(c)
                            ),
                          ];

                        localStorage.setItem("visibleColumns", JSON.stringify(updated));
                        return updated;
                      });
                    }}
                  />
                  <span className="ms-2">Show Spent / Max </span>
                </Label>
              </div>

              <div className="d-flex mb-2">
                <Label>
                  <Input
                    type="checkbox"
                    checked={showCompletedReleased}
                    onChange={() => {
                      setShowCompletedReleased(prev => {
                        const newValue = !prev;
                        localStorage.setItem("showCompletedReleased", String(newValue)); // 🔥 Save permanently
                        return newValue;
                      });
                    }}
                  />
                  <span className="ms-2">Show Released</span>
                </Label>
              </div>
              <div className="d-flex mb-2">
                <Label>
                  <Input
                    type="checkbox"
                    checked={showNotInScope}
                    onChange={() => {
                      setshowNotInScope(prev => {
                        const newValue = !prev;
                        localStorage.setItem("showNotInScope", String(newValue)); // 🔥 Save permanently
                        return newValue;
                      });
                    }}
                  />
                  <span className="ms-2">Show Not In Scope</span>
                </Label>
              </div>
              <FormGroup check className="d-flex mb-2 align-items-center">
                <Label>
                  <Input
                    type="checkbox"
                    checked={showOnDays}
                    onChange={() => {
                      setShowOnDays(prev => {
                        const newValue = !prev;
                        localStorage.setItem("showOnDays", String(newValue));
                        return newValue;
                      });
                    }}
                  />
                  <span className="ms-2">Show in Hours</span>
                </Label>
              </FormGroup>
              <FormGroup check className="d-flex mb-2 align-items-center">
                <Label>
                  <Input
                    type="checkbox"
                    checked={visibleColumns.includes("hours")}
                    onChange={() => {
                      setVisibleColumns(prev => {
                        const updated = prev.includes("hours")
                          ? prev.filter(c => c !== "hours")
                          : [...prev, "hours"];

                        localStorage.setItem("visibleColumns", JSON.stringify(updated)); // 🔥 Save permanently
                        return updated;
                      });
                    }}
                  />
                  <span className="ms-2">Show Time</span>
                </Label>
              </FormGroup>
              <hr />
              <h6>Due Color Settings</h6>

              {Object.keys(dueColorSettings).map(key => (<>
                <div key={key} className="d-flex align-items-center mb-2">
                  <Label style={{ width: "160px" }}>{key}</Label>
                  <Input
                    type="color"
                    value={dueColorSettings[key]}
                    onChange={(e) => {
                      const updated = {
                        ...dueColorSettings,
                        [key]: e.target.value,
                      };
                      setDueColorSettings(updated);
                      localStorage.setItem("dueColorSettings", JSON.stringify(updated));
                    }}
                    style={{ width: "60px", height: "35px", padding: 2 }}
                  />


                </div>
              </>
              ))}

              <div className="mt-3">
                <Button
                  color="secondary"
                  size="sm"
                  onClick={() => {
                    setDueColorSettings(DEFAULT_DUE_COLORS);
                    localStorage.setItem(
                      "dueColorSettings",
                      JSON.stringify(DEFAULT_DUE_COLORS)
                    );
                  }}
                >
                  Reset to Default
                </Button>
              </div>




            </ModalBody>
            <ModalFooter>
              <Button color="primary" onClick={() => {
                // Save to localStorage permanently
                localStorage.setItem("visibleColumns", JSON.stringify(visibleColumns));
                localStorage.setItem("showCompletedReleased", String(showCompletedReleased));
                localStorage.setItem("showNotInScope", String(showNotInScope));
                localStorage.setItem("useWeekdays", String(useWeekdays));
                localStorage.setItem("showOnDays", String(showOnDays));
                localStorage.setItem("showOnpercetbar", String(showOnpercetbar));

                setSettingsOpen(false); // close modal
              }}>
                Apply
              </Button>
            </ModalFooter>

          </Modal>

          <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)} centered>
            <ModalHeader toggle={() => setDeleteModalOpen(false)}>
              Confirm Delete
            </ModalHeader>

            <ModalBody>
              Are you sure you want to delete this child subtask?
            </ModalBody>

            <ModalFooter>
              <Button color="secondary" outline onClick={() => setDeleteModalOpen(false)}>
                Cancel
              </Button>

              <Button color="danger" onClick={confirmDeleteChildSubTask}>
                Delete
              </Button>
            </ModalFooter>
          </Modal>


          <Modal isOpen={showDeleteModal} toggle={closeDeleteModal} centered>
            <div className="p-4 text-center">
              <h5 className="fw-bold text-danger"><WarningIcon />  Delete Task & Subtasks?</h5>
              <p className="mt-2 text-muted">
                Are you sure you want to delete this <b>Task and all Subtasks?</b><br />
              </p>

              <div className="d-flex justify-content-center gap-3 mt-3">

                <Button color="secondary" onClick={closeDeleteModal}>
                  Cancel
                </Button>

                <Button color="danger" onClick={handleDeleteTask}>
                  Yes, Delete
                </Button>

              </div>
            </div>
          </Modal>

          <Modal
            isOpen={showDeleteSubTaskModal}
            toggle={() => setShowDeleteSubTaskModal(false)}
            centered
          >
            <ModalHeader toggle={() => setShowDeleteSubTaskModal(false)}>
              Confirm Delete
            </ModalHeader>

            <ModalBody>
              Are you sure you want to delete this subtask?
            </ModalBody>

            <ModalFooter>
              <Button
                color="secondary"
                outline
                onClick={() => setShowDeleteSubTaskModal(false)}
              >
                Cancel
              </Button>

              <Button color="danger" onClick={handleDeleteSubTask}>
                Delete
              </Button>
            </ModalFooter>
          </Modal>



          <Modal
            isOpen={logModalOpen}
            toggle={() => setLogModalOpen(false)}
            centered
            size="xl"
          >
            <ModalHeader
              toggle={() => setLogModalOpen(false)}
              style={{
                background: "#343a40",
                color: "white",
                fontWeight: 600,
                fontSize: "18px",
                padding: "15px 20px",
              }}
            >
              Overall Log – {selectedLogEntity?.title || `${selectedLogEntity?.type} #${selectedLogEntity?.id}`}
            </ModalHeader>

            <ModalBody
              style={{
                background: "#f3f4f6",
                padding: "20px",
                borderRadius: "0 0 10px 10px",
              }}
            >
              <div
                style={{
                  background: "white",
                  borderRadius: "12px",
                  padding: "15px",
                  boxShadow: "0px 3px 10px rgba(0,0,0,0.08)",
                  overflow: "hidden",
                }}
              >
                <h6
                  style={{
                    marginBottom: "15px",
                    fontWeight: 600,
                    fontSize: "16px",
                    color: "#333",
                  }}
                >
                  Change History
                </h6>

                <Table
                  hover
                  responsive
                  bordered
                  style={{ marginBottom: 0, borderRadius: "10px" }}
                >
                  <thead
                    style={{
                      background: "#f0f2f5",
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#333",
                    }}
                  >
                    <tr>
                      <th style={{ width: "15%" }}>Field</th>
                      <th style={{ width: "20%" }}>Old</th>
                      <th style={{ width: "20%" }}>New</th>
                      <th style={{ width: "20%" }}>User</th>
                      <th style={{ width: "20%" }}>Date</th>
                    </tr>
                  </thead>

                  <tbody style={{ fontSize: "14px" }}>
                    {overallLogs.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="text-center"
                          style={{ padding: "25px", color: "#777" }}
                        >
                          No logs found.
                        </td>
                      </tr>
                    ) : (
                      overallLogs.map((log, index) => (
                        <tr
                          key={log.id ?? index}
                          style={{
                            background: index % 2 === 0 ? "#ffffff" : "#fafafa",
                          }}
                        >
                          {/* FIELD */}
                          <td style={{ fontWeight: 600, color: "#444" }}>
                            {log.fieldName}
                          </td>

                          {/* OLD VALUE */}
                          <td>
                            <span
                              style={{
                                padding: "4px 10px",
                                borderRadius: "6px",
                              }}
                            >
                              {(!log.oldValue || log.oldValue === "00000000-0000-0000-0000-000000000000")
                                ? "-"
                                : log.oldValue}
                            </span>
                          </td>


                          {/* NEW VALUE */}
                          <td>
                            <span
                              style={{
                                padding: "4px 10px",
                                borderRadius: "6px",
                              }}
                            >
                              {log.newValue ?? "-"}
                            </span>
                          </td>

                          {/* USER */}
                          <td style={{ fontWeight: 500 }}>{log.userName}</td>

                          {/* DATE */}
                          <td style={{ color: "#555", fontSize: "12px" }}>
                            {new Date(log.logTime).toLocaleDateString("en-GB")}{" "}
                            {new Date(log.logTime).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </ModalBody>
          </Modal>


          <Modal isOpen={addModalOpen} toggle={toggleAddModal} size="xl" centered>
            <ModalHeader toggle={toggleAddModal}>
              Log Work
            </ModalHeader>

            <ModalBody>
              <Form>

                {/* ===== BASIC INFO ===== */}
                <Row>
                  <Col md={3}>
                    <FormGroup>
                      {/* <Label className="fw-bold">Project</Label> */}
                      <div className="form-control bg-light">
                        {newRowData.projectName || "—"}
                      </div>
                    </FormGroup>
                  </Col>

                  <Col md={3}>
                    <FormGroup>
                      {/* <Label className="fw-bold">Task</Label> */}
                      <div className="form-control bg-light">
                        {newRowData.taskDescription || "—"}
                      </div>
                    </FormGroup>
                  </Col>

                  <Col md={3}>
                    <FormGroup>
                      {/* <Label className="fw-bold">Subtask</Label> */}
                      <div className="form-control bg-light">
                        {newRowData.subTaskDescription || "—"}
                      </div>
                    </FormGroup>
                  </Col>

                  <Col md={3}>
                    <FormGroup>
                      {/* <Label className="fw-bold">Child Task</Label> */}
                      <div className="form-control bg-light">
                        {newRowData.childSubTaskDescription || "—"}
                      </div>
                    </FormGroup>
                  </Col>
                </Row>


                {/* ===== WORK DETAILS ===== */}
                <Row>
                  <Col md={2}>
                    <FormGroup>
                      <Label className="fw-bold">{hoursLabel}</Label>
                      <Input
                        type="select"
                        value={newRowData.hours || ""}
                        invalid={formErrors.hours}
                        onChange={(e) =>
                          setNewRowData({ ...newRowData, hours: Number(e.target.value) })
                        }

                      >
                        <option value="">Hrs</option>
                        {hourOptions.map(h => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>




                  <Col md={2}>
                    <FormGroup>
                      <Label className="fw-bold">{typeLabel}</Label>
                      <Input
                        type="select"
                        value={newRowData.product || ""}
                        invalid={formErrors.product}
                        required
                        onChange={(e) =>
                          setNewRowData({ ...newRowData, product: e.target.value })
                        }

                      >
                        <option value="">Select</option>
                        {getTaskTypeOptions(newRowData.childSubTaskDescription).map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>



                  {category === "DRAFTING" && (
                    <>
                      <Col md={2}>
                        <FormGroup>
                          <Label className="fw-bold">Part Prefix</Label>
                          <Input
                            type="select"
                            value={newRowData.draftingType || ""}
                            invalid={formErrors.draftingType}
                            onChange={(e) =>
                              setNewRowData({ ...newRowData, draftingType: e.target.value })
                            }

                          >
                            <option value="">Select</option>
                            {drawingTypeOptions.map((d) => (
                              <option key={d.code} value={d.code} title={d.full}>
                                {d.label}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>

                      <Col md={2}>
                        <FormGroup>
                          <Label title="No of pages completed" className="fw-bold">Pages done today</Label>
                          {/* <Tooltip
  placement="top"
  open={tooltipOpen}
  target="pagesTooltip"
  toggle={toggletip}
>
         <p>No of pages completed today</p> 
        </Tooltip> */}
                          <Input
                            title="No of pages completed today."
                            type="text"
                            value={newRowData.quantity ?? ""}
                            invalid={formErrors.quantity}
                            onChange={(e) =>
                              setNewRowData({
                                ...newRowData,
                                quantity: Number(e.target.value),
                              })
                            }
                          />

                        </FormGroup>

                      </Col>

                      {/* <Tooltip ></Tooltip> */}

                      <Col md={2}>
                        <FormGroup>
                          <Label className="fw-bold">Total no of pages</Label>

                          <Input
                            title='Total no of pages in one part file'
                            type="text"
                            value={newRowData.totPages ?? ""}
                            invalid={formErrors.totPages}
                            onChange={(e) =>
                              setNewRowData({
                                ...newRowData,
                                totPages: Number(e.target.value),
                              })
                            }
                          />
                        </FormGroup>
                      </Col>


                    </>
                  )}

                </Row>

                {/* ===== REMARKS (EDITABLE) ===== */}
                <Row>
                  <Col md={4}>
                    <FormGroup>
                      <Label className="fw-bold">Part No(s)</Label>
                      <Input
                        type='textarea'
                        rows={3}
                        value={newRowData.part || ""}
                        onChange={(e) =>
                          setNewRowData({ ...newRowData, part: e.target.value })
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md={6}>
                    <FormGroup>
                      <Label className="fw-bold">Remarks</Label>
                      <Input
                        type="textarea"
                        rows={3}
                        value={newRowData.description || ""}
                        onChange={(e) =>
                          setNewRowData({ ...newRowData, description: e.target.value })
                        }
                        placeholder="Add remarks / work description here..."
                      />
                    </FormGroup>
                  </Col>


                </Row>

              </Form>
            </ModalBody>


            <ModalFooter className="justify-content-between">
              <Button color="secondary" outline onClick={toggleAddModal}>
                Cancel
              </Button>

              <Button
                color="success"
                disabled={isSaving}
                onClick={handleSave}
              >
                <CheckCircleIcon className="me-1" />
                {isSaving ? "Saving..." : "Save"}
              </Button>

            </ModalFooter>
          </Modal>



          <Modal
            isOpen={spentModalOpen}
            toggle={() => setSpentModalOpen(false)}
            centered
            size="lg"
          >
            <ModalHeader toggle={() => setSpentModalOpen(false)}>
              🕒 Spent Hours – {spentEntityTitle}
            </ModalHeader>

            <ModalBody>
              {spentLogs.length === 0 ? (
                <Alert color="info">No spent hour records found</Alert>
              ) : (
                <>
                  <Table bordered hover responsive>
                    <thead className="table-light">
                      <tr>
                        <th>User</th>
                        <th>Worked Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(userHourMap).map(
                        ([user, hours]) => (
                          <tr key={user}>
                            <td>{user}</td>
                            <td className="fw-bold">
                              {hours} H
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th>Total</th>
                        <th className="text-success">
                          {totalWorked} H
                        </th>
                      </tr>
                    </tfoot>
                  </Table>

                  <hr />


                </>
              )}
            </ModalBody>
          </Modal>


        </CardBody>
      </Card>
      {inputModalOpen && entity && userID && (
        <ClientInputModal
          isOpen={inputModalOpen}
          toggle={() => {
            setInputModalOpen(false);
            setSelectedEntity(null);
          }}

          // toggle={async () => {
          //   setInputModalOpen(false);
          //   setSelectedEntity(null);

          //   // 🔥 Ensure DB state is reflected after close
          //   if (selectedProjectId) {
          //     await fetchProjectTasks(selectedProjectId);
          //   }
          // }}

          entityType={entity.type}
          entityId={entity.id}

          isClientInputEnabled={
            entity.type === "task"
              ? projects
                .find(p => p.id === selectedProjectId)
                ?.tasks?.find(t => t.id === entity.id)
                ?.isClientInputEnabled ?? false
              : projects
                .find(p => p.id === selectedProjectId)
                ?.tasks
                ?.flatMap(t => t.subTasks || [])
                .find(st => st.id === entity.id)
                ?.isClientInputEnabled ?? false
          }

          /** 🔥 THIS IS THE FIX THAT PREVENTS UNCHECK ON REOPEN */
          onToggle={(enabled: boolean) => {
            setProjects(prev =>
              prev.map(p =>
                p.id !== selectedProjectId
                  ? p
                  : {
                    ...p,
                    tasks: p.tasks?.map(t => {
                      // TASK
                      if (entity.type === "task" && t.id === entity.id) {
                        return { ...t, isClientInputEnabled: enabled };
                      }

                      // SUBTASK
                      if (entity.type === "subtask") {
                        return {
                          ...t,
                          subTasks: t.subTasks.map(st =>
                            st.id === entity.id
                              ? { ...st, isClientInputEnabled: enabled }
                              : st
                          )
                        };
                      }

                      return t;
                    })
                  }
              )
            );
          }}

          canCreate={true}
        />
      )}







    </Container>
  );
};

export default TeamLeadProjectsWithTasks;
